import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, AreaSeries, BarSeries, BaselineSeries, LineStyle } from 'lightweight-charts';
import {
  ArrowLeft, BarChart2, BarChart3, TrendingUp, Calculator, Bell, BookOpen, Activity,
  Layers, MousePointer, Minus, Crosshair, Type, Square, RotateCcw,
  Play, Pause, SkipForward, Sun, Moon, Keyboard, Calendar, Camera,
  Trash2, Plus, X, Eye, EyeOff, Undo2, Redo2, Hash, ArrowRight,
  Maximize2, GitBranch, Triangle, PenTool, ArrowUpRight, MessageSquare,
  Ruler, Clock, List, Star, ChevronDown, ChevronRight, Volume2
} from 'lucide-react';
import {
  calcSMA, calcEMA, calcBollingerBands, calcRSI, calcMACD, calcATR, calcADX,
  calcHeikinAshi, calcStochastic, calcParabolicSAR, calcSuperTrend,
  calcKeltnerChannels, calcDonchianChannels, calcHistoricalVolatility,
  calcVWAP, calcVWAPBands, calcOBV, calcADLine, calcCMF, calcMFI,
  calcDelta, calcCumulativeDelta, calcVolumeProfile,
  calcRenko, calcKagi, calcLineBreak, calcRangeBars, calcPointFigure,
  findPivots, detectAllPatterns
} from './chartCalculations';
import {
  TIMEFRAMES, CHART_TYPES, CHART_TYPE_GROUPS, INDICATORS, ALL_INDICATORS,
  DRAWING_TOOLS, ALL_DRAWING_TOOLS, DRAWING_COLORS, LINE_WIDTHS,
  COMPARISON_MODES, LAYOUT_OPTIONS, TICKER_PRESETS,
  THEMES, KEYBOARD_SHORTCUTS, ALERT_SOUNDS, COMPARISON_COLORS
} from './chartConstants';
import MiniChartPane from './MiniChartPane';

const API_URL = process.env.REACT_APP_API_URL || '';

// ===== SUB-COMPONENTS =====
function SidePanel({ title, show, onClose, width, colors, children }) {
  if (!show) return null;
  return (
    <div className={`${width} border-r overflow-y-auto p-3 flex-shrink-0`} style={{ background: colors.panel, borderColor: colors.border }}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-bold">{title}</span>
        <button onClick={onClose} className="p-0.5 rounded hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
      </div>
      {children}
    </div>
  );
}

function Modal({ title, show, onClose, colors, children, width = 'max-w-lg' }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div className={`relative w-full ${width} mx-4 rounded-lg p-4 shadow-xl`}
        style={{ background: colors.panel, color: colors.text }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <span className="font-bold">{title}</span>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ToolGroup({ label, tools, activeTool, onSelect, colors, expanded, onToggle }) {
  return (
    <div className="mb-1">
      <button onClick={onToggle} className="flex items-center gap-1 w-full text-[10px] py-0.5 px-1 rounded hover:opacity-80"
        style={{ color: colors.textMuted }}>
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <span className="uppercase font-semibold">{label}</span>
      </button>
      {expanded && (
        <div className="flex flex-wrap gap-0.5 pl-2 mt-0.5">
          {tools.map(dt => (
            <button key={dt.value} onClick={() => onSelect(dt.value)}
              className={`p-1 rounded transition ${activeTool === dt.value ? 'bg-blue-600 text-white' : ''}`}
              style={activeTool !== dt.value ? { color: colors.textMuted } : {}} title={dt.label}>
              <dt.icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ========== MAIN COMPONENT ==========
export default function TradingChart({ ticker: initialTicker, onBack }) {
  // ===== THEME =====
  const [theme, setTheme] = useState(() => localStorage.getItem('chart_theme') || 'dark');
  const colors = THEMES[theme];
  const toggleTheme = () => setTheme(t => { const n = t === 'dark' ? 'light' : 'dark'; localStorage.setItem('chart_theme', n); return n; });

  // ===== CHART STATE =====
  const [ticker, setTicker] = useState(initialTicker || 'AAPL');
  const [tickerInput, setTickerInput] = useState(initialTicker || 'AAPL');
  const [period, setPeriod] = useState('1y');
  const [chartType, setChartType] = useState('candlestick');
  const [priceScale, setPriceScale] = useState('normal');
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [legendData, setLegendData] = useState({});
  const [chartError, setChartError] = useState(null);

  // ===== CHART TYPE MENU =====
  const [showChartTypeMenu, setShowChartTypeMenu] = useState(false);
  const [showChartTypeFlyout, setShowChartTypeFlyout] = useState(false);
  const chartTypeBtnRef = useRef(null);
  const [showPeriodFlyout, setShowPeriodFlyout] = useState(false);
  const periodBtnRef = useRef(null);

  // ===== PANELS =====
  const [showIndicatorPanel, setShowIndicatorPanel] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showRiskCalc, setShowRiskCalc] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [showPerformance, setShowPerformance] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [showDrawingTools, setShowDrawingTools] = useState(false);
  const [showWatchlist, setShowWatchlist] = useState(false);

  // ===== DRAWING STATE =====
  const [activeTool, setActiveTool] = useState('cursor');
  const [drawingStart, setDrawingStart] = useState(null);
  const [drawings, setDrawings] = useState(() => { try { return JSON.parse(localStorage.getItem('chart_drawings')) || []; } catch { return []; } });
  const [drawingColor, setDrawingColor] = useState('#2962FF');
  const [drawingWidth, setDrawingWidth] = useState(2);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({ selection: true, lines: true });

  // ===== INDICATORS =====
  const [activeIndicators, setActiveIndicators] = useState([]);

  // ===== ALERTS =====
  const [alerts, setAlerts] = useState(() => { try { return JSON.parse(localStorage.getItem('chart_alerts')) || []; } catch { return []; } });
  const [alertForm, setAlertForm] = useState({ type: 'above', value: '', sound: 'beep' });

  // ===== TRADE JOURNAL =====
  const [trades, setTrades] = useState(() => { try { return JSON.parse(localStorage.getItem('chart_trades')) || []; } catch { return []; } });
  const [tradeForm, setTradeForm] = useState({ ticker: '', type: 'buy', entry: '', exit: '', qty: '', notes: '' });
  const [journalTab, setJournalTab] = useState('journal');

  // ===== POSITIONS =====
  const [positions, setPositions] = useState(() => { try { return JSON.parse(localStorage.getItem('chart_positions')) || []; } catch { return []; } });
  const [posForm, setPosForm] = useState({ ticker: '', type: 'long', entry: '', qty: '', stopLoss: '', target: '' });

  // ===== AI ANALYSIS =====
  const [aiResults, setAiResults] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // ===== RISK CALCULATOR =====
  const [riskInputs, setRiskInputs] = useState({ account: 10000, riskPct: 2, entry: 0, stop: 0, target: 0 });

  // ===== COMPARISON =====
  const [comparisonTickers, setComparisonTickers] = useState([]);
  const [comparisonInput, setComparisonInput] = useState('');
  const [comparisonMode, setComparisonMode] = useState('overlay');
  const comparisonSeriesRef = useRef({});

  // ===== REPLAY =====
  const [replayMode, setReplayMode] = useState(false);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [replaySpeed, setReplaySpeed] = useState(1000);
  const replayTimerRef = useRef(null);

  // ===== WATCHLIST =====
  const [watchlist, setWatchlist] = useState(() => { try { return JSON.parse(localStorage.getItem('chart_watchlist')) || TICKER_PRESETS; } catch { return TICKER_PRESETS; } });
  const [watchlistInput, setWatchlistInput] = useState('');

  // ===== LAYOUT =====
  const [layout, setLayout] = useState(1);
  // Extra pane tickers (pane 2 = 2H/2V/2×2, panes 3&4 = 2×2 only)
  const [pane2Ticker, setPane2Ticker] = useState('MSFT');
  const [pane3Ticker, setPane3Ticker] = useState('GOOGL');
  const [pane4Ticker, setPane4Ticker] = useState('AMZN');
  // Refs for mini panes — used for time-scale sync
  const pane2Ref = useRef(null);
  const pane3Ref = useRef(null);
  const pane4Ref = useRef(null);
  const mainSyncingRef = useRef(false);

  // ===== MODALS =====
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showGoToDate, setShowGoToDate] = useState(false);
  const [goToDateValue, setGoToDateValue] = useState('');
  const [showContextMenu, setShowContextMenu] = useState(null);
  const [toasts, setToasts] = useState([]);

  // ===== REFS =====
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const indicatorSeriesRef = useRef({});
  const rsiContainerRef = useRef(null);
  const rsiChartRef = useRef(null);
  const macdContainerRef = useRef(null);
  const macdChartRef = useRef(null);
  const subChartRefs = useRef({});
  const displayDataRef = useRef([]);

  // ===== TOAST SYSTEM =====
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);

  // ===== PERSISTENCE =====
  useEffect(() => { localStorage.setItem('chart_drawings', JSON.stringify(drawings)); }, [drawings]);
  useEffect(() => { localStorage.setItem('chart_alerts', JSON.stringify(alerts)); }, [alerts]);
  useEffect(() => { localStorage.setItem('chart_trades', JSON.stringify(trades)); }, [trades]);
  useEffect(() => { localStorage.setItem('chart_positions', JSON.stringify(positions)); }, [positions]);
  useEffect(() => { localStorage.setItem('chart_watchlist', JSON.stringify(watchlist)); }, [watchlist]);

  // ===== DATA LOADING =====
  // Convert any time value to a sortable number:
  //   Unix seconds  (number)  → returned as-is
  //   "YYYY-MM-DD"  (string)  → converted to YYYYMMDD integer
  const timeKey = (t) => {
    if (typeof t === 'number') return t;
    if (typeof t === 'string') { const n = Number(t.replace(/-/g, '')); return isNaN(n) ? 0 : n; }
    return 0;
  };
  // Deduplicate and sort any data array before passing to lightweight-charts
  const safeData = (arr) => {
    if (!arr || !arr.length) return [];
    const sorted = [...arr].sort((a, b) => timeKey(a.time) - timeKey(b.time));
    const seen = new Set();
    return sorted.filter(d => {
      const k = String(d.time);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  // Map our timeframe values to backend period params
  const toApiPeriod = (per) => {
    const map = {
      '1d':  '1d',
      '5d':  '5d',
      '1mo': '1mo',
      '3m':  '3mo',
      '6m':  '6mo',
      'ytd': 'ytd',
      '1y':  '1y',
      '3y':  '3y',
      '5y':  '5y',
      'max': 'max',
    };
    return map[per] || per;
  };

  // Generate realistic OHLC from a price series (since backend only returns close price)
  const buildOHLC = (raw) => {
    const result = [];
    for (let i = 0; i < raw.length; i++) {
      const close = raw[i].price ?? raw[i].close;
      const prev = i > 0 ? result[i - 1].close : close;
      // Synthetic open: gap from previous close (±0.3%)
      const open = +(prev * (1 + (Math.random() - 0.5) * 0.006)).toFixed(2);
      // Synthetic high/low using realistic intraday range (~0.5–1.5% of close)
      const range = close * (0.005 + Math.random() * 0.01);
      const high = +(Math.max(open, close) + Math.random() * range).toFixed(2);
      const low  = +(Math.min(open, close) - Math.random() * range).toFixed(2);
      result.push({ ...raw[i], open, high, low, close: +close.toFixed(2) });
    }
    return result;
  };

  // ── localStorage cache helpers ──────────────────────────────────────────
  const CACHE_TTL = 15 * 60 * 1000; // 15 minutes
  const cacheKey  = (sym, per) => `chart_cache_${sym}_${per}`;
  const readCache = (sym, per) => {
    try {
      const raw = localStorage.getItem(cacheKey(sym, per));
      if (!raw) return null;
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(cacheKey(sym, per)); return null; }
      return data;
    } catch { return null; }
  };
  const writeCache = (sym, per, data) => {
    try { localStorage.setItem(cacheKey(sym, per), JSON.stringify({ ts: Date.now(), data })); } catch {}
  };

  const parseAndFormat = (json) => {
    const raw = json.data || json.historical || (Array.isArray(json) ? json : null);
    if (!Array.isArray(raw) || !raw.length) throw new Error('No data');
    const withTime = raw.map(d => ({
      ...d,
      time: d.timestamp ? Math.floor(d.timestamp / 1000) : (d.date || d.time || '').split('T')[0],
    }));
    const _tk = (t) => typeof t === 'number' ? t : Number(String(t).replace(/-/g, '')) || 0;
    withTime.sort((a, b) => _tk(a.time) - _tk(b.time));
    const seen = new Set();
    const deduped = withTime.filter(d => { const k = String(d.time); if (seen.has(k)) return false; seen.add(k); return true; });
    const hasOHLC = deduped[0] && deduped[0].open != null && deduped[0].high != null;
    return hasOHLC
      ? deduped.map(d => ({ time: d.time, open: +d.open, high: +d.high, low: +d.low, close: +d.close, volume: d.volume || 0 }))
      : buildOHLC(deduped).map(d => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.close, volume: d.volume || 0 }));
  };

  const applyFormatted = (formatted) => {
    setHistoricalData(formatted);
    if (formatted.length > 0) {
      const last = formatted[formatted.length - 1];
      setLegendData(last);
      setRiskInputs(prev => ({ ...prev, entry: last.close, stop: +(last.close * 0.95).toFixed(2), target: +(last.close * 1.1).toFixed(2) }));
    }
  };

  const loadData = useCallback(async (sym, per) => {
    // 1. Show cached data instantly (no loading spinner)
    const cached = readCache(sym, per);
    if (cached) {
      applyFormatted(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    // 2. Fetch fresh data in background
    try {
      const apiPeriod = toApiPeriod(per);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      let res;
      try {
        res = await fetch(`${API_URL}/api/chart/${sym}?period=${apiPeriod}`, { signal: controller.signal });
      } finally {
        clearTimeout(timeout);
      }
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const json = await res.json();
      const formatted = parseAndFormat(json);
      writeCache(sym, per, formatted);
      applyFormatted(formatted);
    } catch (err) {
      console.warn('Data load failed:', err.message);
      if (!cached) {
        // No cache available — show fallback random data
        const fallback = [];
        let price = 150;
        const now = new Date();
        for (let i = 250; i >= 0; i--) {
          const d = new Date(now); d.setDate(d.getDate() - i);
          if (d.getDay() === 0 || d.getDay() === 6) continue;
          const change = (Math.random() - 0.48) * 4;
          price = Math.max(50, price + change);
          const o = +price.toFixed(2), range = price * 0.01;
          fallback.push({ time: d.toISOString().split('T')[0], open: o, high: +(o + Math.random() * range).toFixed(2), low: +(o - Math.random() * range).toFixed(2), close: +(o + (Math.random() - 0.5) * range).toFixed(2), volume: Math.floor(Math.random() * 50000000) });
        }
        applyFormatted(fallback);
      }
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadData(ticker, period); }, [ticker, period, loadData]);

  // Clear previous AI analysis whenever the ticker changes
  useEffect(() => { setAiResults(null); }, [ticker]);

  const handleLoadTicker = () => { if (tickerInput.trim()) { setTicker(tickerInput.trim()); } };

  // ===== CHART INITIALIZATION =====
  // useLayoutEffect runs synchronously after the DOM is updated but BEFORE the
  // browser paints. This means clientWidth/clientHeight are already computed
  // (layout is done) so the chart is created with real dimensions on the very
  // first frame — no blank flash, no dependence on a later ResizeObserver tick.
  useLayoutEffect(() => {
    if (!chartContainerRef.current) return;
    const container = chartContainerRef.current;
    let chart, ro, roRaf;
    try {
      const w = container.clientWidth || 600;
      const h = container.clientHeight || 400;
      chart = createChart(container, {
        width: w, height: h,
        layout: { background: { color: colors.bg }, textColor: colors.text, attributionLogo: false },
        grid: { vertLines: { color: colors.gridLines }, horzLines: { color: colors.gridLines } },
        crosshair: { mode: 0, vertLine: { color: '#758696', style: LineStyle.Dashed }, horzLine: { color: '#758696', style: LineStyle.Dashed } },
        rightPriceScale: { borderColor: colors.border, mode: priceScale === 'log' ? 1 : priceScale === 'percentage' ? 2 : 0 },
        timeScale: { borderColor: colors.border, timeVisible: true, secondsVisible: false },
      });
      chartRef.current = chart;
      const candlestick = chart.addSeries(CandlestickSeries, {
        upColor: colors.up, downColor: colors.down, borderVisible: false,
        wickUpColor: colors.up, wickDownColor: colors.down,
      });
      candlestickSeriesRef.current = candlestick;
      chart.subscribeCrosshairMove((param) => {
        if (param.time) {
          const data = param.seriesData.get(candlestick);
          if (data) setLegendData({ open: data.open, high: data.high, low: data.low, close: data.close });
        }
      });
      // ResizeObserver for subsequent layout changes (panel open/close, window
      // resize). entry.contentRect always contains accurate post-layout values,
      // unlike clientWidth which can be stale mid-transition.
      // Capture contentRect values synchronously (accurate at callback time),
      // then defer applyOptions to the next animation frame to avoid the
      // "ResizeObserver loop completed with undelivered notifications" warning.
      ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            cancelAnimationFrame(roRaf);
            roRaf = requestAnimationFrame(() => chart.applyOptions({ width, height }));
          }
        }
      });
      ro.observe(container);

      // Time-scale sync: push main chart range to all mini panes
      chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
        if (!mainSyncingRef.current && range) {
          mainSyncingRef.current = true;
          pane2Ref.current?.setTimeRange(range);
          pane3Ref.current?.setTimeRange(range);
          pane4Ref.current?.setTimeRange(range);
          mainSyncingRef.current = false;
        }
      });

      setChartError(null);
    } catch (e) {
      setChartError(String(e));
    }
    return () => {
      cancelAnimationFrame(roRaf);
      if (ro) ro.disconnect();
      if (chart) chart.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  // ===== PRICE SCALE MODE ($, Log, %) =====
  useEffect(() => {
    if (!chartRef.current) return;
    const mode = priceScale === 'log' ? 1 : priceScale === 'percentage' ? 2 : 0;
    chartRef.current.applyOptions({ rightPriceScale: { mode } });
  }, [priceScale]);

  // ===== CHART DATA UPDATE =====
  useEffect(() => {
    if (!candlestickSeriesRef.current || !historicalData.length) return;
    const chart = chartRef.current;
    const series = candlestickSeriesRef.current;
    try {
    // Remove old indicator series
    Object.values(indicatorSeriesRef.current).forEach(s => { try { chart.removeSeries(s); } catch {} });
    indicatorSeriesRef.current = {};
    // Reset right price scale margins to defaults so that removing the volume
    // indicator (which sets custom margins) doesn't leave the chart invisible.
    try { chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } }); } catch {}

    // Always sanitize before use — removes any duplicate/out-of-order timestamps
    const replayData = safeData(replayMode ? historicalData.slice(0, replayIndex) : historicalData);
    if (!replayData.length) return;

    let displayData;
    // Chart type transforms
    if (chartType === 'heikinashi') displayData = safeData(calcHeikinAshi(replayData));
    else if (chartType === 'renko') displayData = safeData(calcRenko(replayData));
    else if (chartType === 'kagi') displayData = safeData(calcKagi(replayData));
    else if (chartType === 'linebreak') displayData = safeData(calcLineBreak(replayData));
    else if (chartType === 'rangebars') displayData = safeData(calcRangeBars(replayData));
    else if (chartType === 'pointfigure') displayData = safeData(calcPointFigure(replayData));
    else displayData = replayData;
    displayDataRef.current = displayData;

    // ---- Non-candlestick series types ----
    const LINE_TYPES = ['line', 'linemarkers', 'stepline', 'area', 'hlcarea', 'baseline', 'columns', 'highlow', 'bars'];
    if (LINE_TYPES.includes(chartType)) {
      series.applyOptions({ visible: false });
      const closeData = safeData(displayData.map(d => ({ time: d.time, value: d.close })));

      if (chartType === 'bars') {
        const bs = chart.addSeries(BarSeries, {
          upColor: colors.up, downColor: colors.down,
          openVisible: true, thinBars: false,
        });
        bs.setData(safeData(displayData.map(d => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.close }))));
        indicatorSeriesRef.current['__mainBars'] = bs;

      } else if (chartType === 'line') {
        const ls = chart.addSeries(LineSeries, { color: colors.accent, lineWidth: 2 });
        ls.setData(closeData);
        indicatorSeriesRef.current['__mainLine'] = ls;

      } else if (chartType === 'linemarkers') {
        const ls = chart.addSeries(LineSeries, { color: colors.accent, lineWidth: 2, pointMarkersVisible: true, pointMarkersRadius: 3 });
        ls.setData(closeData);
        indicatorSeriesRef.current['__mainLine'] = ls;

      } else if (chartType === 'stepline') {
        const ls = chart.addSeries(LineSeries, { color: colors.accent, lineWidth: 2, lineType: 2 }); // 2 = WithSteps
        ls.setData(closeData);
        indicatorSeriesRef.current['__mainLine'] = ls;

      } else if (chartType === 'area') {
        const as = chart.addSeries(AreaSeries, { topColor: colors.accent + '40', bottomColor: colors.accent + '05', lineColor: colors.accent, lineWidth: 2 });
        as.setData(closeData);
        indicatorSeriesRef.current['__mainArea'] = as;

      } else if (chartType === 'hlcarea') {
        const hlcData = safeData(displayData.map(d => ({ time: d.time, value: (d.high + d.low + d.close) / 3 })));
        const as = chart.addSeries(AreaSeries, { topColor: colors.up + '40', bottomColor: colors.up + '05', lineColor: colors.up, lineWidth: 2 });
        as.setData(hlcData);
        indicatorSeriesRef.current['__mainArea'] = as;

      } else if (chartType === 'baseline') {
        const midPrice = displayData.length
          ? (Math.max(...displayData.map(d => d.high)) + Math.min(...displayData.map(d => d.low))) / 2
          : 0;
        const bs = chart.addSeries(BaselineSeries, {
          baseValue: { type: 'price', price: midPrice },
          topLineColor: colors.up, topFillColor1: colors.up + '40', topFillColor2: colors.up + '10',
          bottomLineColor: colors.down, bottomFillColor1: colors.down + '10', bottomFillColor2: colors.down + '40',
        });
        bs.setData(closeData);
        indicatorSeriesRef.current['__mainBaseline'] = bs;

      } else if (chartType === 'columns') {
        const colData = safeData(displayData.map((d, i, arr) => ({
          time: d.time, value: d.close,
          color: i === 0 || d.close >= arr[i - 1].close ? colors.up + 'cc' : colors.down + 'cc',
        })));
        const hs = chart.addSeries(HistogramSeries, { priceScaleId: 'right', base: 0 });
        hs.setData(colData);
        indicatorSeriesRef.current['__mainColumns'] = hs;

      } else if (chartType === 'highlow') {
        // High-low: show wick only by making open === close === midpoint
        const hlData = safeData(displayData.map(d => {
          const mid = +((d.high + d.low) / 2).toFixed(4);
          return { time: d.time, open: mid, high: d.high, low: d.low, close: mid };
        }));
        const cs = chart.addSeries(CandlestickSeries, {
          upColor: colors.up, downColor: colors.up,
          borderUpColor: colors.up, borderDownColor: colors.down,
          wickUpColor: colors.up, wickDownColor: colors.down, borderVisible: false,
        });
        cs.setData(hlData);
        indicatorSeriesRef.current['__mainHighLow'] = cs;
      }

    } else if (chartType === 'hollowcandle') {
      series.applyOptions({
        visible: true, upColor: 'transparent', downColor: colors.down,
        borderUpColor: colors.up, borderDownColor: colors.down,
        wickUpColor: colors.up, wickDownColor: colors.down, borderVisible: true,
      });
      series.setData(displayData);

    } else if (chartType === 'volumecandles') {
      // Candle opacity scales with relative volume
      const vols = displayData.map(d => d.volume || 1);
      const avgVol = vols.reduce((a, b) => a + b, 0) / (vols.length || 1);
      const maxVol = Math.max(...vols);
      const volData = safeData(displayData.map(d => {
        const vol = d.volume || avgVol;
        const opacity = Math.round(Math.min(255, 80 + 175 * (vol / maxVol))).toString(16).padStart(2, '0');
        const isUp = d.close >= d.open;
        return {
          ...d,
          color: (isUp ? colors.up : colors.down) + opacity,
          borderColor: isUp ? colors.up : colors.down,
          wickColor: isUp ? colors.up : colors.down,
        };
      }));
      series.applyOptions({ visible: true, borderVisible: true });
      series.setData(volData);

    } else {
      // candlestick / heikinashi / renko / kagi / linebreak / rangebars / pointfigure
      series.applyOptions({
        visible: true, upColor: colors.up, downColor: colors.down,
        borderVisible: false, wickUpColor: colors.up, wickDownColor: colors.down,
      });
      series.setData(displayData);
    }

    const data = replayData; // already sanitized above
    const sd = (arr) => safeData(arr); // shorthand
    // Alternative chart types (Renko, Kagi, P&F etc.) use sequential synthetic
    // timestamps. Skip overlay indicators to prevent time-axis mismatch.
    const isAltChartType = ['renko', 'kagi', 'linebreak', 'rangebars', 'pointfigure'].includes(chartType);
    if (isAltChartType) {
      try { chart.timeScale().fitContent(); } catch {}
      requestAnimationFrame(() => { try { chart.timeScale().fitContent(); } catch {} });
      return;
    }
    // ===== OVERLAY INDICATORS =====
    if (activeIndicators.includes('sma20')) {
      const s = chart.addSeries(LineSeries, { color: '#FF6B6B', lineWidth: 1 }); s.setData(sd(calcSMA(data, 20))); indicatorSeriesRef.current.sma20 = s;
    }
    if (activeIndicators.includes('sma50')) {
      const s = chart.addSeries(LineSeries, { color: '#4ECDC4', lineWidth: 1 }); s.setData(sd(calcSMA(data, 50))); indicatorSeriesRef.current.sma50 = s;
    }
    if (activeIndicators.includes('sma200')) {
      const s = chart.addSeries(LineSeries, { color: '#45B7D1', lineWidth: 1 }); s.setData(sd(calcSMA(data, 200))); indicatorSeriesRef.current.sma200 = s;
    }
    if (activeIndicators.includes('ema20')) {
      const s = chart.addSeries(LineSeries, { color: '#FFEAA7', lineWidth: 1 }); s.setData(sd(calcEMA(data, 20))); indicatorSeriesRef.current.ema20 = s;
    }
    if (activeIndicators.includes('bollinger')) {
      const bb = calcBollingerBands(data);
      const su = chart.addSeries(LineSeries, { color: '#9C27B0', lineWidth: 1, lineStyle: LineStyle.Dashed }); su.setData(sd(bb.upper));
      const sm = chart.addSeries(LineSeries, { color: '#9C27B0', lineWidth: 1 }); sm.setData(sd(bb.middle));
      const sl = chart.addSeries(LineSeries, { color: '#9C27B0', lineWidth: 1, lineStyle: LineStyle.Dashed }); sl.setData(sd(bb.lower));
      indicatorSeriesRef.current.bbU = su; indicatorSeriesRef.current.bbM = sm; indicatorSeriesRef.current.bbL = sl;
    }
    if (activeIndicators.includes('volume')) {
      const vs = chart.addSeries(HistogramSeries, { priceScaleId: 'volume', priceFormat: { type: 'volume' } });
      vs.setData(sd(data.map(d => ({ time: d.time, value: d.volume || 0, color: d.close >= d.open ? colors.up + '60' : colors.down + '60' }))));
      chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
      // Push the main price scale up so it doesn't overlap the volume histogram.
      chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.1, bottom: 0.25 } });
      indicatorSeriesRef.current.volume = vs;
    }
    if (activeIndicators.includes('vwap')) {
      const s = chart.addSeries(LineSeries, { color: '#FF9800', lineWidth: 2 }); s.setData(sd(calcVWAP(data))); indicatorSeriesRef.current.vwap = s;
    }
    if (activeIndicators.includes('psar')) {
      const s = chart.addSeries(LineSeries, { color: '#00BCD4', lineWidth: 0, pointMarkersVisible: true, pointMarkersRadius: 2 });
      s.setData(sd(calcParabolicSAR(data))); indicatorSeriesRef.current.psar = s;
    }
    if (activeIndicators.includes('supertrend')) {
      const s = chart.addSeries(LineSeries, { color: '#26a69a', lineWidth: 2 }); s.setData(sd(calcSuperTrend(data))); indicatorSeriesRef.current.supertrend = s;
    }
    if (activeIndicators.includes('keltner')) {
      const kc = calcKeltnerChannels(data);
      const su = chart.addSeries(LineSeries, { color: '#00E5FF', lineWidth: 1, lineStyle: LineStyle.Dashed }); su.setData(sd(kc.upper));
      const sl = chart.addSeries(LineSeries, { color: '#00E5FF', lineWidth: 1, lineStyle: LineStyle.Dashed }); sl.setData(sd(kc.lower));
      indicatorSeriesRef.current.kcU = su; indicatorSeriesRef.current.kcL = sl;
    }
    if (activeIndicators.includes('donchian')) {
      const dc = calcDonchianChannels(data);
      const su = chart.addSeries(LineSeries, { color: '#FFEB3B', lineWidth: 1 }); su.setData(sd(dc.upper));
      const sl = chart.addSeries(LineSeries, { color: '#FFEB3B', lineWidth: 1 }); sl.setData(sd(dc.lower));
      const sm = chart.addSeries(LineSeries, { color: '#FFEB3B', lineWidth: 1, lineStyle: LineStyle.Dotted }); sm.setData(sd(dc.middle));
      indicatorSeriesRef.current.dcU = su; indicatorSeriesRef.current.dcL = sl; indicatorSeriesRef.current.dcM = sm;
    }
    if (activeIndicators.includes('vwapbands')) {
      const vb = calcVWAPBands(data);
      const sv = chart.addSeries(LineSeries, { color: '#FF9800', lineWidth: 2 }); sv.setData(sd(vb.vwap));
      const su = chart.addSeries(LineSeries, { color: '#FF9800', lineWidth: 1, lineStyle: LineStyle.Dashed }); su.setData(sd(vb.upper));
      const sl = chart.addSeries(LineSeries, { color: '#FF9800', lineWidth: 1, lineStyle: LineStyle.Dashed }); sl.setData(sd(vb.lower));
      indicatorSeriesRef.current.vwapV = sv; indicatorSeriesRef.current.vwapU = su; indicatorSeriesRef.current.vwapL = sl;
    }
      setChartError(null);
      // Trigger an immediate repaint so the chart paints newly-set series data
      // even when no side-effecting applyOptions call (e.g. volume scale) is present.
      try { chart.timeScale().fitContent(); } catch {}
      // Also defer a second fitContent to the next frame: this corrects the
      // visible range after lightweight-charts has fully processed the new data
      // (needed when switching timeframes so the old range isn't kept).
      const chartForFit = chart;
      requestAnimationFrame(() => {
        try { chartForFit.timeScale().fitContent(); } catch {}
      });
    } catch (e) {
      setChartError(String(e));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalData, chartType, activeIndicators, theme, replayMode, replayIndex]);

  // ===== DRAWINGS (separate effect — does NOT call fitContent so scrolling is preserved) =====
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !candlestickSeriesRef.current) return;
    const data = displayDataRef.current;
    // Remove only drawing series
    Object.keys(indicatorSeriesRef.current).forEach(key => {
      if (key.startsWith('draw_') || key.startsWith('fib_')) {
        try { chart.removeSeries(indicatorSeriesRef.current[key]); } catch {}
        delete indicatorSeriesRef.current[key];
      }
    });
    // Re-add drawing series
    drawings.filter(d => d.type === 'horizontal').forEach(d => {
      const s = chart.addSeries(LineSeries, { color: d.color || drawingColor, lineWidth: d.width || 1, lineStyle: LineStyle.Dashed, priceLineVisible: false });
      const startTime = data[0]?.time; const endTime = data[data.length - 1]?.time;
      if (startTime && endTime) { s.setData([{ time: startTime, value: d.price }, { time: endTime, value: d.price }]); }
      indicatorSeriesRef.current[`draw_${d.id}`] = s;
    });
    drawings.filter(d => d.type === 'trendline').forEach(d => {
      const s = chart.addSeries(LineSeries, { color: d.color || drawingColor, lineWidth: d.width || 2, priceLineVisible: false });
      const pts = [{ time: d.startTime, value: d.startPrice }, { time: d.endTime, value: d.endPrice }]
        .sort((a, b) => timeKey(a.time) - timeKey(b.time));
      s.setData(pts);
      indicatorSeriesRef.current[`draw_${d.id}`] = s;
    });
    drawings.filter(d => d.type === 'fibonacci').forEach(d => {
      const levels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
      const fibColors = ['#787b86', '#f44336', '#ff9800', '#ffeb3b', '#4caf50', '#2196f3', '#787b86'];
      levels.forEach((lvl, idx) => {
        const price = d.endPrice + (d.startPrice - d.endPrice) * lvl;
        const s = chart.addSeries(LineSeries, { color: fibColors[idx], lineWidth: 1, lineStyle: LineStyle.Dotted, priceLineVisible: false });
        if (data[0]?.time && data[data.length - 1]?.time) { s.setData([{ time: data[0].time, value: price }, { time: data[data.length - 1].time, value: price }]); }
        indicatorSeriesRef.current[`fib_${d.id}_${idx}`] = s;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawings, drawingColor]);

  // ===== RSI SUB-CHART =====
  useEffect(() => {
    if (!activeIndicators.includes('rsi') || !rsiContainerRef.current || !historicalData.length) {
      if (rsiChartRef.current) { rsiChartRef.current.remove(); rsiChartRef.current = null; }
      return;
    }
    const rsiChart = createChart(rsiContainerRef.current, {
      height: 200, layout: { background: { color: colors.bg }, textColor: colors.textMuted, attributionLogo: false },
      grid: { vertLines: { visible: false }, horzLines: { color: colors.gridLines } },
      rightPriceScale: { borderColor: colors.border }, timeScale: { visible: false },
    });
    rsiChartRef.current = rsiChart;
    const rsiRaw = calcRSI(historicalData);
    const rsiData = safeData(rsiRaw);
    const rsiSeries = rsiChart.addSeries(LineSeries, { color: '#E040FB', lineWidth: 1 });
    rsiSeries.setData(rsiData);
    // Overbought/Oversold lines
    if (rsiData.length > 1) {
      const ob = rsiChart.addSeries(LineSeries, { color: '#ef535066', lineWidth: 1, lineStyle: LineStyle.Dashed });
      ob.setData([{ time: rsiData[0].time, value: 70 }, { time: rsiData[rsiData.length - 1].time, value: 70 }]);
      const os = rsiChart.addSeries(LineSeries, { color: '#26a69a66', lineWidth: 1, lineStyle: LineStyle.Dashed });
      os.setData([{ time: rsiData[0].time, value: 30 }, { time: rsiData[rsiData.length - 1].time, value: 30 }]);
    }
    if (chartRef.current) {
      chartRef.current.timeScale().subscribeVisibleLogicalRangeChange(range => {
        if (range) rsiChart.timeScale().setVisibleLogicalRange(range);
      });
    }
    return () => { rsiChart.remove(); rsiChartRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndicators.includes('rsi'), historicalData, theme]);

  // ===== MACD SUB-CHART =====
  useEffect(() => {
    if (!activeIndicators.includes('macd') || !macdContainerRef.current || !historicalData.length) {
      if (macdChartRef.current) { macdChartRef.current.remove(); macdChartRef.current = null; }
      return;
    }
    const macdChart = createChart(macdContainerRef.current, {
      height: 200, layout: { background: { color: colors.bg }, textColor: colors.textMuted, attributionLogo: false },
      grid: { vertLines: { visible: false }, horzLines: { color: colors.gridLines } },
      rightPriceScale: { borderColor: colors.border }, timeScale: { visible: false },
    });
    macdChartRef.current = macdChart;
    const macdRaw = calcMACD(historicalData);
    const macdData = { macd: safeData(macdRaw.macd), signal: safeData(macdRaw.signal), histogram: safeData(macdRaw.histogram) };
    const macdLine = macdChart.addSeries(LineSeries, { color: '#2962FF', lineWidth: 1 }); macdLine.setData(macdData.macd);
    const sigLine = macdChart.addSeries(LineSeries, { color: '#FF6D00', lineWidth: 1 }); sigLine.setData(macdData.signal);
    const histSeries = macdChart.addSeries(HistogramSeries, {}); histSeries.setData(macdData.histogram);
    if (chartRef.current) {
      chartRef.current.timeScale().subscribeVisibleLogicalRangeChange(range => {
        if (range) macdChart.timeScale().setVisibleLogicalRange(range);
      });
    }
    return () => { macdChart.remove(); macdChartRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndicators.includes('macd'), historicalData, theme]);

  // ===== ADDITIONAL SUB-CHARTS (Stochastic, ADX, ATR, MFI, OBV, etc.) =====
  const subChartIndicators = ['stochastic', 'adx', 'atr', 'hvol', 'obv', 'adline', 'cmf', 'mfi', 'delta', 'cumdelta'];
  useEffect(() => {
    // Remove old sub-charts
    Object.entries(subChartRefs.current).forEach(([key, ref]) => {
      if (ref?.chart && !activeIndicators.includes(key)) { ref.chart.remove(); delete subChartRefs.current[key]; }
    });
    if (!historicalData.length) return;
    const cleanups = [];
    subChartIndicators.forEach(ind => {
      if (!activeIndicators.includes(ind)) return;
      const containerId = `subchart-${ind}`;
      const container = document.getElementById(containerId);
      if (!container || subChartRefs.current[ind]) return;
      const sc = createChart(container, {
        height: 160, layout: { background: { color: colors.bg }, textColor: colors.textMuted, attributionLogo: false },
        grid: { vertLines: { visible: false }, horzLines: { color: colors.gridLines } },
        rightPriceScale: { borderColor: colors.border }, timeScale: { visible: false },
      });
      subChartRefs.current[ind] = { chart: sc };
      let seriesData;
      const sd2 = (arr) => {
        if (!arr || !arr.length) return [];
        const sorted = [...arr].sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
        const seen = new Set();
        return sorted.filter(d => { const k = String(d.time); if (seen.has(k)) return false; seen.add(k); return true; });
      };
      switch (ind) {
        case 'stochastic': {
          const st = calcStochastic(historicalData);
          const kLine = sc.addSeries(LineSeries, { color: '#2962FF', lineWidth: 1 }); kLine.setData(sd2(st.k));
          const dLine = sc.addSeries(LineSeries, { color: '#FF6D00', lineWidth: 1 }); dLine.setData(sd2(st.d));
          break; }
        case 'adx': seriesData = sd2(calcADX(historicalData)); break;
        case 'atr': seriesData = sd2(calcATR(historicalData)); break;
        case 'hvol': seriesData = sd2(calcHistoricalVolatility(historicalData)); break;
        case 'obv': seriesData = sd2(calcOBV(historicalData)); break;
        case 'adline': seriesData = sd2(calcADLine(historicalData)); break;
        case 'cmf': seriesData = sd2(calcCMF(historicalData)); break;
        case 'mfi': seriesData = sd2(calcMFI(historicalData)); break;
        case 'delta': {
          const d = sd2(calcDelta(historicalData));
          const hs = sc.addSeries(HistogramSeries, {}); hs.setData(d);
          break; }
        case 'cumdelta': seriesData = sd2(calcCumulativeDelta(historicalData)); break;
        default: break;
      }
      if (seriesData && seriesData.length) {
        const ls = sc.addSeries(LineSeries, { color: '#E040FB', lineWidth: 1 }); ls.setData(seriesData);
      }
      if (chartRef.current) {
        chartRef.current.timeScale().subscribeVisibleLogicalRangeChange(range => {
          if (range) sc.timeScale().setVisibleLogicalRange(range);
        });
      }
      cleanups.push(() => { sc.remove(); delete subChartRefs.current[ind]; });
    });
    return () => { cleanups.forEach(fn => fn()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndicators, historicalData, theme]);

  // ===== INDICATOR TOGGLE =====
  const toggleIndicator = (ind) => setActiveIndicators(prev => prev.includes(ind) ? prev.filter(i => i !== ind) : [...prev, ind]);

  // ===== DRAWING FUNCTIONS =====
  const saveUndoState = () => { setUndoStack(prev => [...prev.slice(-49), JSON.stringify(drawings)]); setRedoStack([]); };
  const undo = () => {
    if (!undoStack.length) return;
    setRedoStack(prev => [...prev, JSON.stringify(drawings)]);
    setDrawings(JSON.parse(undoStack[undoStack.length - 1]));
    setUndoStack(prev => prev.slice(0, -1));
  };
  const redo = () => {
    if (!redoStack.length) return;
    setUndoStack(prev => [...prev, JSON.stringify(drawings)]);
    setDrawings(JSON.parse(redoStack[redoStack.length - 1]));
    setRedoStack(prev => prev.slice(0, -1));
  };

  const handleChartClick = useCallback((e) => {
    if (!chartRef.current || !candlestickSeriesRef.current || activeTool === 'cursor' || activeTool === 'crosshair') return;
    const chart = chartRef.current;
    const rect = chartContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const time = chart.timeScale().coordinateToTime(x);
    const price = candlestickSeriesRef.current.coordinateToPrice(y);
    if (!time || !price) return;

    if (activeTool === 'horizontal') {
      saveUndoState();
      setDrawings(prev => [...prev, { id: Date.now(), type: 'horizontal', price, color: drawingColor, width: drawingWidth }]);
      addToast(`Horizontal line at $${price.toFixed(2)}`);
    } else if (activeTool === 'vertical') {
      saveUndoState();
      setDrawings(prev => [...prev, { id: Date.now(), type: 'vertical', time, color: drawingColor, width: drawingWidth }]);
    } else if (activeTool === 'text' || activeTool === 'callout') {
      const text = prompt('Enter text:');
      if (text) { saveUndoState(); setDrawings(prev => [...prev, { id: Date.now(), type: activeTool, time, price, text, color: drawingColor }]); }
    } else if (['trendline', 'ray', 'extended', 'channel', 'fibonacci', 'fibext', 'fibfan', 'fibarc', 'fibtime', 'fibchannel',
      'gannfan', 'gannsquare', 'ganngrid', 'elliott_impulse', 'elliott_corrective', 'elliott_projection',
      'pat_hs', 'pat_double', 'pat_triangle', 'pat_wedge', 'pat_flag',
      'pitchfork', 'regchannel', 'rectangle', 'pricerange', 'daterange', 'arrow'].includes(activeTool)) {
      if (!drawingStart) {
        setDrawingStart({ time, price });
      } else {
        saveUndoState();
        setDrawings(prev => [...prev, {
          id: Date.now(), type: activeTool,
          startTime: drawingStart.time, startPrice: drawingStart.price,
          endTime: time, endPrice: price,
          color: drawingColor, width: drawingWidth,
        }]);
        setDrawingStart(null);
        if (activeTool === 'trendline') addToast('Trendline drawn');
        else if (activeTool === 'fibonacci') addToast('Fibonacci retracement drawn');
        else addToast(`${activeTool} drawn`);
      }
    }
  }, [activeTool, drawingStart, drawingColor, drawingWidth, addToast, drawings]);

  const clearDrawings = () => { saveUndoState(); setDrawings([]); addToast('All drawings cleared'); };

  // ===== SCREENSHOT =====
  const captureScreenshot = useCallback(() => {
    if (!chartContainerRef.current) return;
    try {
      const canvas = chartContainerRef.current.querySelector('canvas');
      if (canvas) {
        const link = document.createElement('a');
        link.download = `${ticker}_chart_${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL();
        link.click();
        addToast('Screenshot saved');
      }
    } catch { addToast('Screenshot failed', 'error'); }
  }, [ticker, addToast]);

  // ===== ALERTS =====
  const addAlert = () => {
    const value = parseFloat(alertForm.value);
    if (isNaN(value)) return;
    setAlerts(prev => [...prev, { id: Date.now(), ticker, type: alertForm.type, value, sound: alertForm.sound, active: true, triggered: false }]);
    setAlertForm(f => ({ ...f, value: '' }));
    addToast(`Alert set: ${alertForm.type} $${value.toFixed(2)}`);
  };
  const deleteAlert = (id) => setAlerts(prev => prev.filter(a => a.id !== id));
  const toggleAlert = (id) => setAlerts(prev => prev.map(a => a.id === id ? { ...a, active: !a.active } : a));

  const playAlertSound = (type) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const sound = ALERT_SOUNDS.find(s => s.value === type) || ALERT_SOUNDS[0];
      osc.type = sound.type; osc.frequency.value = sound.freq;
      gain.gain.setValueAtTime(0.3, ctx.currentTime); gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.5);
    } catch {}
  };

  // Alert checking
  useEffect(() => {
    if (!historicalData.length) return;
    const interval = setInterval(() => {
      const lastPrice = historicalData[historicalData.length - 1]?.close;
      if (!lastPrice) return;
      setAlerts(prev => prev.map(a => {
        if (!a.active || a.triggered || a.ticker !== ticker) return a;
        const triggered = (a.type === 'above' && lastPrice >= a.value) || (a.type === 'below' && lastPrice <= a.value);
        if (triggered) { playAlertSound(a.sound); addToast(`Alert: ${ticker} ${a.type} $${a.value}`, 'warning'); }
        return triggered ? { ...a, triggered: true } : a;
      }));
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalData, ticker]);

  // ===== TRADE JOURNAL =====
  const addTrade = () => {
    const entry = parseFloat(tradeForm.entry), exit = parseFloat(tradeForm.exit), qty = parseInt(tradeForm.qty);
    if (!tradeForm.ticker || isNaN(entry) || isNaN(qty)) return;
    const pnl = exit ? (tradeForm.type === 'buy' ? (exit - entry) * qty : (entry - exit) * qty) : null;
    setTrades(prev => [...prev, { id: Date.now(), ...tradeForm, entry, exit: exit || null, qty, pnl, date: new Date().toISOString().split('T')[0] }]);
    setTradeForm({ ticker: '', type: 'buy', entry: '', exit: '', qty: '', notes: '' });
    addToast('Trade logged');
  };
  const deleteTrade = (id) => setTrades(prev => prev.filter(t => t.id !== id));

  // ===== POSITIONS =====
  const addPosition = () => {
    const entry = parseFloat(posForm.entry), qty = parseInt(posForm.qty);
    if (!posForm.ticker || isNaN(entry) || isNaN(qty)) return;
    setPositions(prev => [...prev, { id: Date.now(), ...posForm, entry, qty, stopLoss: parseFloat(posForm.stopLoss) || null, target: parseFloat(posForm.target) || null }]);
    setPosForm({ ticker: '', type: 'long', entry: '', qty: '', stopLoss: '', target: '' });
    addToast('Position opened');
  };
  const closePosition = (id) => setPositions(prev => prev.filter(p => p.id !== id));

  // ===== PERFORMANCE STATS =====
  const calcPerfStats = () => {
    const closed = trades.filter(t => t.pnl !== null);
    if (!closed.length) return null;
    const wins = closed.filter(t => t.pnl > 0), losses = closed.filter(t => t.pnl <= 0);
    const totalPnl = closed.reduce((s, t) => s + t.pnl, 0);
    const grossProfit = wins.reduce((s, t) => s + t.pnl, 0);
    const grossLoss = Math.abs(losses.reduce((s, t) => s + t.pnl, 0));
    let maxDD = 0, peak = 0, equity = 0;
    closed.forEach(t => { equity += t.pnl; if (equity > peak) peak = equity; const dd = peak - equity; if (dd > maxDD) maxDD = dd; });
    return {
      total: closed.length, winRate: ((wins.length / closed.length) * 100).toFixed(1),
      profitFactor: grossLoss > 0 ? (grossProfit / grossLoss).toFixed(2) : '∞',
      totalPnl: totalPnl.toFixed(2), avgWin: wins.length ? (grossProfit / wins.length).toFixed(2) : '0',
      avgLoss: losses.length ? (grossLoss / losses.length).toFixed(2) : '0',
      bestTrade: closed.length ? Math.max(...closed.map(t => t.pnl)).toFixed(2) : '0',
      worstTrade: closed.length ? Math.min(...closed.map(t => t.pnl)).toFixed(2) : '0',
      maxDrawdown: maxDD.toFixed(2),
    };
  };
  const perfStats = calcPerfStats();

  // ===== AI ANALYSIS =====
  const runAIAnalysis = () => {
    if (!historicalData.length) return;
    setAiLoading(true);
    setTimeout(() => {
      const data = historicalData;
      const patterns = detectAllPatterns(data);
      // S/R
      const { highs, lows } = findPivots(data);
      const resistanceLevels = highs.sort((a, b) => b.price - a.price).slice(0, 3).map(h => ({ price: h.price, strength: 70 + Math.random() * 25 }));
      const supportLevels = lows.sort((a, b) => a.price - b.price).slice(0, 3).map(l => ({ price: l.price, strength: 70 + Math.random() * 25 }));
      // Trend
      const sma20 = calcSMA(data, 20), sma50 = calcSMA(data, 50);
      const lastClose = data[data.length - 1].close;
      const sma20Last = sma20.length ? sma20[sma20.length - 1].value : lastClose;
      const sma50Last = sma50.length ? sma50[sma50.length - 1].value : lastClose;
      const adxData = calcADX(data);
      const adxLast = adxData.length ? adxData[adxData.length - 1].value : 20;
      const trendDir = lastClose > sma20Last && sma20Last > sma50Last ? 'Bullish' : lastClose < sma20Last && sma20Last < sma50Last ? 'Bearish' : 'Neutral';
      const trendStrength = Math.min(100, adxLast * 2);
      // Forecast
      const recent = data.slice(-30);
      const avgChange = recent.reduce((s, d, i) => i > 0 ? s + (d.close - recent[i - 1].close) / recent[i - 1].close : s, 0) / (recent.length - 1);
      const forecastTarget = lastClose * (1 + avgChange * 10);
      const forecastChange = ((forecastTarget - lastClose) / lastClose * 100).toFixed(2);
      // Volatility
      const atrData = calcATR(data);
      const atrLast = atrData.length ? atrData[atrData.length - 1].value : 0;
      const atrPct = (atrLast / lastClose * 100).toFixed(2);
      const hvData = calcHistoricalVolatility(data);
      const hvLast = hvData.length ? hvData[hvData.length - 1].value : 0;
      // Breakout
      const bb = calcBollingerBands(data);
      const bbWidth = bb.upper.length ? (bb.upper[bb.upper.length - 1].value - bb.lower[bb.lower.length - 1].value) / bb.middle[bb.middle.length - 1].value : 0;
      const squeeze = bbWidth < 0.05;
      const breakoutProb = squeeze ? 75 + Math.random() * 20 : 30 + Math.random() * 30;
      const breakoutDir = lastClose > sma20Last ? 'bullish' : 'bearish';
      // Anomalies
      const anomalies = [];
      const avgVol = data.reduce((s, d) => s + (d.volume || 0), 0) / data.length;
      data.slice(-10).forEach(d => {
        if ((d.volume || 0) > avgVol * 3) anomalies.push({ type: 'Volume Spike', date: d.time, severity: 'high' });
        if (Math.abs(d.high - d.low) > atrLast * 2.5) anomalies.push({ type: 'Wide Range', date: d.time, severity: 'medium' });
      });
      setAiResults({
        patterns, support: supportLevels, resistance: resistanceLevels,
        trend: { direction: trendDir, strength: trendStrength.toFixed(0), adx: adxLast.toFixed(1) },
        forecast: { target: forecastTarget.toFixed(2), change: forecastChange, confidence: 65, range: { upper: (forecastTarget * 1.05).toFixed(2), lower: (forecastTarget * 0.95).toFixed(2) } },
        volatility: { historical: hvLast.toFixed(1), atrPct, regime: hvLast > 30 ? 'High' : hvLast > 15 ? 'Normal' : 'Low' },
        breakout: { probability: breakoutProb.toFixed(0), direction: breakoutDir, squeeze },
        anomalies,
      });
      setAiLoading(false);
    }, 500);
  };

  // ===== RISK CALCULATOR =====
  const riskResults = (() => {
    const { account, riskPct, entry, stop, target } = riskInputs;
    const riskAmt = account * (riskPct / 100);
    const riskPerShare = Math.abs(entry - stop);
    const shares = riskPerShare > 0 ? Math.floor(riskAmt / riskPerShare) : 0;
    const posValue = shares * entry;
    const profit = shares * Math.abs(target - entry);
    const rr = riskPerShare > 0 ? Math.abs(target - entry) / riskPerShare : 0;
    return { riskAmt: riskAmt.toFixed(2), shares, posValue: posValue.toFixed(2), profit: profit.toFixed(2), rr: rr.toFixed(2) };
  })();

  const autoFillRisk = () => {
    if (!historicalData.length) return;
    const last = historicalData[historicalData.length - 1];
    const atrData = calcATR(historicalData);
    const atr = atrData.length ? atrData[atrData.length - 1].value : last.close * 0.02;
    setRiskInputs(prev => ({ ...prev, entry: +last.close.toFixed(2), stop: +(last.close - atr * 1.5).toFixed(2), target: +(last.close + atr * 3).toFixed(2) }));
  };

  // ===== COMPARISON =====
  const addComparisonTicker = async () => {
    const sym = comparisonInput.trim().toUpperCase();
    if (!sym || sym === ticker || comparisonTickers.includes(sym)) return;
    try {
      const res = await fetch(`${API_URL}/api/stock/${sym}?period=${period}`);
      const json = await res.json();
      const raw = json.historical || json.data || json;
      if (!Array.isArray(raw) || !raw.length) throw new Error('No data');
      const formatted = raw.map(d => ({ time: d.date || d.time, close: d.close ?? d.price })).sort((a, b) => a.time < b.time ? -1 : 1);
      if (chartRef.current) {
        const colorIdx = comparisonTickers.length % COMPARISON_COLORS.length;
        const baseFirst = historicalData[0]?.close || 1;
        const compFirst = formatted[0]?.close || 1;
        let seriesData;
        if (comparisonMode === 'overlay') {
          seriesData = formatted.map(d => ({ time: d.time, value: d.close }));
        } else if (comparisonMode === 'relative') {
          seriesData = formatted.map(d => ({ time: d.time, value: ((d.close / compFirst) - 1) * 100 }));
        } else if (comparisonMode === 'spread') {
          const mainMap = {};
          historicalData.forEach(d => { mainMap[d.time] = d.close; });
          seriesData = formatted.filter(d => mainMap[d.time]).map(d => ({ time: d.time, value: mainMap[d.time] - d.close }));
        } else if (comparisonMode === 'ratio') {
          const mainMap = {};
          historicalData.forEach(d => { mainMap[d.time] = d.close; });
          seriesData = formatted.filter(d => mainMap[d.time] && d.close > 0).map(d => ({ time: d.time, value: mainMap[d.time] / d.close }));
        }
        const s = chartRef.current.addSeries(LineSeries, { color: COMPARISON_COLORS[colorIdx], lineWidth: 2, priceScaleId: comparisonMode === 'overlay' ? 'right' : 'comparison' });
        s.setData(seriesData || []);
        comparisonSeriesRef.current[sym] = s;
      }
      setComparisonTickers(prev => [...prev, sym]);
      setComparisonInput('');
    } catch { addToast(`Failed to load ${sym}`, 'error'); }
  };
  const removeComparisonTicker = (sym) => {
    if (comparisonSeriesRef.current[sym] && chartRef.current) {
      try { chartRef.current.removeSeries(comparisonSeriesRef.current[sym]); } catch {}
      delete comparisonSeriesRef.current[sym];
    }
    setComparisonTickers(prev => prev.filter(t => t !== sym));
  };

  // ===== REPLAY =====
  const toggleReplay = () => {
    if (replayMode) { setReplayMode(false); setReplayPlaying(false); setReplayIndex(0); }
    else { setReplayMode(true); setReplayIndex(Math.max(20, Math.floor(historicalData.length * 0.3))); }
  };
  useEffect(() => {
    if (replayPlaying && replayMode) {
      replayTimerRef.current = setInterval(() => {
        setReplayIndex(prev => { if (prev >= historicalData.length) { setReplayPlaying(false); return prev; } return prev + 1; });
      }, replaySpeed);
    }
    return () => { if (replayTimerRef.current) clearInterval(replayTimerRef.current); };
  }, [replayPlaying, replayMode, replaySpeed, historicalData.length]);

  // ===== WATCHLIST =====
  const addToWatchlist = () => {
    const sym = watchlistInput.trim().toUpperCase();
    if (sym && !watchlist.includes(sym)) { setWatchlist(prev => [...prev, sym]); setWatchlistInput(''); }
  };
  const removeFromWatchlist = (sym) => setWatchlist(prev => prev.filter(s => s !== sym));

  // ===== GO TO DATE =====
  const goToDate = () => {
    if (!goToDateValue || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    const target = goToDateValue;
    const idx = historicalData.findIndex(d => d.time >= target);
    if (idx >= 0) { ts.scrollToPosition(idx - historicalData.length + 20, false); }
    setShowGoToDate(false);
  };

  // ===== CONTEXT MENU =====
  const handleContextMenu = (e) => { e.preventDefault(); setShowContextMenu({ x: e.clientX, y: e.clientY }); };

  // ===== KEYBOARD SHORTCUTS =====
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      const key = e.key.toLowerCase();
      if (e.ctrlKey || e.metaKey) {
        if (key === 'z') { e.preventDefault(); undo(); }
        else if (key === 'y') { e.preventDefault(); redo(); }
        else if (key === 's') { e.preventDefault(); captureScreenshot(); }
        return;
      }
      switch (key) {
        case '?': setShowShortcuts(s => !s); break;
        case 't': toggleTheme(); break;
        case 'v': setShowChartTypeFlyout(v => !v); break;
        case 'i': openSinglePanel(setShowIndicatorPanel, showIndicatorPanel); break;
        case 'a': openSinglePanel(setShowAIPanel, showAIPanel); break;
        case 'r': openSinglePanel(setShowRiskCalc, showRiskCalc); break;
        case 'b': openSinglePanel(setShowAlerts, showAlerts); break;
        case 'j': openSinglePanel(setShowJournal, showJournal); break;
        case 'p': openSinglePanel(setShowPerformance, showPerformance); break;
        case 'd': openSinglePanel(setShowDrawingTools, showDrawingTools); break;
        case 'c': openSinglePanel(setShowComparison, showComparison); break;
        case 'w': openSinglePanel(setShowWatchlist, showWatchlist); break;
        case 'g': setShowGoToDate(s => !s); break;
        case 'f': chartRef.current?.timeScale().fitContent(); break;
        case ' ': if (replayMode) { e.preventDefault(); setReplayPlaying(p => !p); } break;
        case 'arrowleft': if (replayMode) setReplayIndex(prev => Math.max(1, prev - 1)); break;
        case 'arrowright': if (replayMode) setReplayIndex(prev => Math.min(historicalData.length, prev + 1)); break;
        case 'delete': case 'backspace': if (drawings.length) { saveUndoState(); setDrawings(prev => prev.slice(0, -1)); } break;
        case 'escape':
          setShowShortcuts(false); setShowGoToDate(false); setShowContextMenu(null); setShowChartTypeMenu(false); setShowChartTypeFlyout(false);
          setActiveTool('cursor'); setDrawingStart(null);
          if (replayMode) { setReplayMode(false); setReplayPlaying(false); }
          break;
        default:
          if (key >= '1' && key <= '9') {
            const idx = parseInt(key) - 1;
            if (idx < TIMEFRAMES.length) setPeriod(TIMEFRAMES[idx].value);
          }
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayMode, drawings, historicalData.length]);

  // ===== CLOSE ALL PANELS =====
  const closeAllPanels = () => {
    setShowChartTypeFlyout(false);
    setShowPeriodFlyout(false);
    setShowIndicatorPanel(false); setShowAIPanel(false); setShowRiskCalc(false);
    setShowAlerts(false); setShowJournal(false); setShowPerformance(false);
    setShowDrawingTools(false); setShowComparison(false); setShowWatchlist(false);
  };
  // Only one panel open at a time: close all others, then open the target
  // (or close it if it was already open — acts as a toggle).
  const openSinglePanel = (setter, isOpen) => {
    closeAllPanels();
    if (!isOpen) setter(true);
  };

  // ===== RENDER =====
  return (
    <div className="flex flex-col overflow-hidden" style={{ background: colors.bg, color: colors.text, height: 'calc(100vh - 64px)' }}
      onContextMenu={handleContextMenu} onClick={() => { setShowContextMenu(null); setShowChartTypeMenu(false); }}>

      {/* ===== TOOLBAR (single bar) — only visible when replay active or indicators are tagged ===== */}
      {(replayMode || activeIndicators.length > 0) && <div className="flex flex-shrink-0 relative z-10" style={{ background: colors.panel, borderBottom: `1px solid ${colors.border}` }}>

        {/* SINGLE ROW: Ticker + OHLCV + Timeframes + Scale + Layout + Drawing + Replay */}
        <div className="flex items-center justify-center h-10 min-h-[40px] overflow-x-auto w-full">
        <div className="flex items-center gap-1.5 px-2 flex-shrink-0">
        {/* Replay Controls (visible only when replay is active) */}
        {replayMode && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[10px] font-semibold flex items-center gap-0.5 flex-shrink-0" style={{ color: '#a855f7' }}>
              <RotateCcw className="w-3 h-3" />Replay
            </span>
            <button onClick={() => setReplayIndex(prev => Math.max(1, prev - 10))} className="p-0.5 rounded text-[10px]" style={{ color: colors.text }}>-10</button>
            <button onClick={() => setReplayIndex(prev => Math.max(1, prev - 1))} className="p-0.5 rounded text-[10px]" style={{ color: colors.text }}>-1</button>
            <button onClick={() => setReplayPlaying(p => !p)} className="p-0.5 rounded" style={{ color: colors.text }}>
              {replayPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => setReplayIndex(prev => Math.min(prev + 1, historicalData.length))} className="p-0.5 rounded text-[10px]" style={{ color: colors.text }}>+1</button>
            <button onClick={() => setReplayIndex(prev => Math.min(prev + 10, historicalData.length))} className="p-0.5 rounded text-[10px]" style={{ color: colors.text }}>+10</button>
            <select value={replaySpeed} onChange={e => setReplaySpeed(parseInt(e.target.value))}
              className="text-[10px] rounded px-0.5" style={{ background: colors.border, color: colors.text, border: 'none' }}>
              <option value={2000}>0.5x</option><option value={1000}>1x</option><option value={500}>2x</option><option value={200}>5x</option>
            </select>
            <span className="text-[10px]" style={{ color: colors.textMuted }}>{replayIndex}/{historicalData.length}</span>
            <input type="range" min={1} max={historicalData.length} value={replayIndex}
              onChange={e => setReplayIndex(parseInt(e.target.value))} className="w-16 h-1" />
            {historicalData[replayIndex - 1] && <span className="text-[10px]" style={{ color: colors.textMuted }}>{historicalData[replayIndex - 1].time}</span>}
          </div>
        )}

        {/* Active Indicators Tags */}
        <div className="flex gap-0.5 ml-auto flex-wrap">
          {activeIndicators.map(ind => (
            <span key={ind} className="px-1 py-0.5 rounded text-[9px] flex items-center gap-0.5"
              style={{ background: colors.border, color: colors.textMuted }}>
              {ALL_INDICATORS.find(i => i.value === ind)?.label || ind}
              <button onClick={() => toggleIndicator(ind)} style={{ color: colors.down }}>×</button>
            </span>
          ))}
        </div>
        </div>{/* end inner content */}
        </div>{/* end SINGLE ROW */}
      </div>}{/* end TOOLBAR wrapper */}

      {/* ===== MAIN CONTENT ===== */}
      <div className="flex flex-1 overflow-hidden" style={{ minHeight: 0 }}>

        {/* ===== LEFT TOOLBAR (panel toggles + utilities, top-to-bottom) ===== */}
        <div className="flex flex-col items-center gap-1 py-2 px-1 flex-shrink-0 border-r overflow-y-auto"
          style={{ background: colors.panel, borderColor: colors.border, width: 48 }}>
          {/* Back to feed */}
          {onBack && (
            <button onClick={onBack}
              className="w-9 h-9 flex items-center justify-center rounded transition"
              style={{ color: colors.textMuted }}
              title="Back">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="w-6 h-px my-0.5" style={{ background: colors.border }} />

          {/* Scale group: $ / Log / % */}
          {[{ l: '$', v: 'normal', title: 'Normal' }, { l: 'Log', v: 'log', title: 'Log scale' }, { l: '%', v: 'percentage', title: 'Percentage' }].map(ps => (
            <button key={ps.v} onClick={() => setPriceScale(ps.v)}
              className={`w-9 h-7 text-[10px] rounded transition ${priceScale === ps.v ? 'bg-blue-600 text-white' : ''}`}
              style={priceScale !== ps.v ? { color: colors.textMuted } : {}} title={ps.title}>{ps.l}</button>
          ))}
          <div className="w-6 h-px my-0.5" style={{ background: colors.border }} />

          {/* Layout group: 1×1 / 2H / 2V / 2×2 */}
          {LAYOUT_OPTIONS.map(lo => (
            <button key={lo.value} onClick={() => setLayout(lo.value)}
              className={`w-9 h-7 text-[10px] rounded transition ${layout === lo.value ? 'bg-blue-600 text-white' : ''}`}
              style={layout !== lo.value ? { color: colors.textMuted } : {}} title={lo.label}>{lo.label}</button>
          ))}
          <div className="w-6 h-px my-0.5" style={{ background: colors.border }} />

          {/* Period button — opens flyout to the right */}
          <div className="relative">
            <button
              ref={periodBtnRef}
              onClick={e => { e.stopPropagation(); const wasOpen = showPeriodFlyout; closeAllPanels(); if (!wasOpen) setShowPeriodFlyout(true); }}
              className="w-9 h-9 flex items-center justify-center rounded transition"
              style={{ background: showPeriodFlyout ? colors.accent + '30' : 'transparent', color: showPeriodFlyout ? colors.accent : colors.textMuted }}
              title="Period"
            >
              <Clock className="w-5 h-5" />
            </button>
            {showPeriodFlyout && (
              <div
                className="fixed z-[300] rounded shadow-2xl py-1.5 min-w-[120px] overflow-y-auto"
                style={{
                  left: (periodBtnRef.current?.getBoundingClientRect().right ?? 48) + 4,
                  top: periodBtnRef.current?.getBoundingClientRect().top ?? 100,
                  maxHeight: '80vh',
                  background: colors.panel,
                  border: `1px solid ${colors.border}`,
                }}
                onClick={e => e.stopPropagation()}
              >
                {TIMEFRAMES.map(tf => (
                  <button
                    key={tf.value}
                    onClick={() => { setPeriod(tf.value); setShowPeriodFlyout(false); }}
                    className="w-full text-left px-3 py-1.5 text-xs flex items-center justify-between transition hover:opacity-80"
                    style={{
                      background: period === tf.value ? colors.accent + '20' : 'transparent',
                      color: period === tf.value ? colors.accent : colors.text,
                    }}
                  >
                    {tf.label}
                    {period === tf.value && <span className="text-[9px]">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chart Type button — opens flyout to the right */}
          <div className="relative">
            <button
              ref={chartTypeBtnRef}
              onClick={e => { e.stopPropagation(); const wasOpen = showChartTypeFlyout; closeAllPanels(); if (!wasOpen) setShowChartTypeFlyout(true); }}
              className="w-9 h-9 flex items-center justify-center rounded transition"
              style={{ background: showChartTypeFlyout ? colors.accent + '30' : 'transparent', color: showChartTypeFlyout ? colors.accent : colors.textMuted }}
              title="Chart Type (V)"
            >
              <BarChart2 className="w-5 h-5" />
            </button>
            {showChartTypeFlyout && (
              <div
                className="fixed z-[300] rounded shadow-2xl py-1.5 min-w-[180px] overflow-y-auto"
                style={{
                  left: (chartTypeBtnRef.current?.getBoundingClientRect().right ?? 48) + 4,
                  top: chartTypeBtnRef.current?.getBoundingClientRect().top ?? 100,
                  maxHeight: '80vh',
                  background: colors.panel,
                  border: `1px solid ${colors.border}`,
                }}
                onClick={e => e.stopPropagation()}
              >
                {CHART_TYPE_GROUPS.map((group, gi) => (
                  <div key={group.label}>
                    {gi > 0 && <div className="h-px my-1" style={{ background: colors.border }} />}
                    <div className="px-3 py-0.5 text-[9px] uppercase font-bold tracking-wider" style={{ color: colors.textMuted }}>
                      {group.label}
                    </div>
                    {group.types.map(ct => (
                      <button
                        key={ct.value}
                        onClick={() => { setChartType(ct.value); setShowChartTypeFlyout(false); }}
                        className="w-full text-left px-3 py-1.5 text-xs flex items-center justify-between transition hover:opacity-80"
                        style={{
                          background: chartType === ct.value ? colors.accent + '20' : 'transparent',
                          color: chartType === ct.value ? colors.accent : colors.text,
                        }}
                      >
                        {ct.label}
                        {chartType === ct.value && <span className="text-[9px]">✓</span>}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {[
            { icon: BarChart3, label: 'Indicators', toggle: () => openSinglePanel(setShowIndicatorPanel, showIndicatorPanel), active: showIndicatorPanel, key: 'I' },
            { icon: TrendingUp, label: 'AI', toggle: () => openSinglePanel(setShowAIPanel, showAIPanel), active: showAIPanel, key: 'A' },
            { icon: Calculator, label: 'Risk', toggle: () => openSinglePanel(setShowRiskCalc, showRiskCalc), active: showRiskCalc, key: 'R' },
            { icon: Bell, label: 'Alerts', toggle: () => openSinglePanel(setShowAlerts, showAlerts), active: showAlerts, key: 'B' },
            { icon: BookOpen, label: 'Journal', toggle: () => openSinglePanel(setShowJournal, showJournal), active: showJournal, key: 'J' },
            { icon: Activity, label: 'Perf', toggle: () => openSinglePanel(setShowPerformance, showPerformance), active: showPerformance, key: 'P' },
            { icon: Layers, label: 'Compare', toggle: () => openSinglePanel(setShowComparison, showComparison), active: showComparison, key: 'C' },
            { icon: List, label: 'Watch', toggle: () => openSinglePanel(setShowWatchlist, showWatchlist), active: showWatchlist, key: 'W' },
            { icon: PenTool, label: 'Draw', toggle: () => openSinglePanel(setShowDrawingTools, showDrawingTools), active: showDrawingTools, key: 'D' },
          ].map(({ icon: Icon, label, toggle, active, key }) => (
            <button key={label} onClick={toggle}
              className="w-9 h-9 flex items-center justify-center rounded transition"
              style={{ background: active ? colors.accent + '30' : 'transparent', color: active ? colors.accent : colors.textMuted }}
              title={`${label} (${key})`}>
              <Icon className="w-5 h-5" />
            </button>
          ))}
          <div className="w-6 h-px my-0.5" style={{ background: colors.border }} />

          {/* Quick Drawing Tools */}
          {ALL_DRAWING_TOOLS.slice(0, 8).map(dt => (
            <button key={dt.value} onClick={() => { setActiveTool(dt.value); setDrawingStart(null); }}
              className={`w-9 h-9 flex items-center justify-center rounded transition ${activeTool === dt.value ? 'bg-blue-600 text-white' : ''}`}
              style={activeTool !== dt.value ? { color: colors.textMuted } : {}} title={dt.label}>
              <dt.icon className="w-4 h-4" />
            </button>
          ))}
          <button onClick={undo} className="w-9 h-9 flex items-center justify-center rounded transition"
            style={{ color: undoStack.length ? colors.text : colors.textMuted }} title="Undo (Ctrl+Z)"><Undo2 className="w-4 h-4" /></button>
          <button onClick={redo} className="w-9 h-9 flex items-center justify-center rounded transition"
            style={{ color: redoStack.length ? colors.text : colors.textMuted }} title="Redo (Ctrl+Y)"><Redo2 className="w-4 h-4" /></button>
          {drawings.length > 0 && (
            <button onClick={clearDrawings} className="w-9 h-9 flex items-center justify-center rounded transition"
              style={{ color: colors.down }} title="Clear drawings"><Trash2 className="w-4 h-4" /></button>
          )}
          <div className="w-6 h-px my-0.5" style={{ background: colors.border }} />

          {/* Replay toggle */}
          <button onClick={toggleReplay}
            className={`w-9 h-9 flex items-center justify-center rounded transition ${replayMode ? 'bg-purple-600 text-white' : ''}`}
            style={!replayMode ? { color: colors.textMuted } : {}} title="Replay mode">
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Volume toggle */}
          <button onClick={() => toggleIndicator('volume')}
            className={`w-9 h-9 flex items-center justify-center rounded transition ${activeIndicators.includes('volume') ? 'bg-blue-600 text-white' : ''}`}
            style={!activeIndicators.includes('volume') ? { color: colors.textMuted } : {}} title="Toggle volume">
            <Volume2 className="w-4 h-4" />
          </button>
          <div className="w-6 h-px my-0.5" style={{ background: colors.border }} />
          <button onClick={toggleTheme} className="w-9 h-9 flex items-center justify-center rounded" style={{ color: colors.textMuted }} title="Theme (T)">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button onClick={captureScreenshot} className="w-9 h-9 flex items-center justify-center rounded" style={{ color: colors.textMuted }} title="Screenshot (Ctrl+S)">
            <Camera className="w-5 h-5" />
          </button>
          <button onClick={() => setShowGoToDate(s => !s)} className="w-9 h-9 flex items-center justify-center rounded" style={{ color: colors.textMuted }} title="Go to date (G)">
            <Calendar className="w-5 h-5" />
          </button>
          <button onClick={() => setShowShortcuts(s => !s)} className="w-9 h-9 flex items-center justify-center rounded" style={{ color: colors.textMuted }} title="Shortcuts (?)">
            <Keyboard className="w-5 h-5" />
          </button>
        </div>

        {/* ===== DRAWING TOOLS SIDE PANEL (LEFT) ===== */}
        {showDrawingTools && (
          <div className="w-36 border-r overflow-y-auto p-1.5 flex-shrink-0" style={{ background: colors.panel, borderColor: colors.border }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase" style={{ color: colors.textMuted }}>Drawing Tools</span>
              <button onClick={() => setShowDrawingTools(false)} className="p-0.5"><X className="w-3 h-3" style={{ color: colors.textMuted }} /></button>
            </div>
            {Object.entries(DRAWING_TOOLS).map(([group, tools]) => (
              <ToolGroup key={group} label={group} tools={tools} activeTool={activeTool}
                onSelect={(val) => { setActiveTool(val); setDrawingStart(null); }}
                colors={colors} expanded={expandedGroups[group] !== false}
                onToggle={() => setExpandedGroups(prev => ({ ...prev, [group]: prev[group] === false ? true : false }))} />
            ))}
            {/* Color Picker */}
            <div className="mt-2 border-t pt-2" style={{ borderColor: colors.border }}>
              <span className="text-[10px] uppercase font-semibold" style={{ color: colors.textMuted }}>Color</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {DRAWING_COLORS.map(c => (
                  <button key={c} onClick={() => setDrawingColor(c)}
                    className={`w-4 h-4 rounded-full border-2 ${drawingColor === c ? 'border-white' : 'border-transparent'}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
            {/* Line Width */}
            <div className="mt-2 border-t pt-2" style={{ borderColor: colors.border }}>
              <span className="text-[10px] uppercase font-semibold" style={{ color: colors.textMuted }}>Width</span>
              <div className="flex gap-1 mt-1">
                {LINE_WIDTHS.map(w => (
                  <button key={w} onClick={() => setDrawingWidth(w)}
                    className={`w-6 h-6 rounded flex items-center justify-center ${drawingWidth === w ? 'bg-blue-600' : ''}`}
                    style={drawingWidth !== w ? { background: colors.border } : {}}>
                    <div style={{ width: '16px', height: `${w}px`, background: drawingColor, borderRadius: '1px' }} />
                  </button>
                ))}
              </div>
            </div>
            {/* Actions */}
            <div className="mt-2 border-t pt-2 flex flex-col gap-1" style={{ borderColor: colors.border }}>
              <button onClick={undo} disabled={!undoStack.length} className="flex items-center gap-1 text-[10px] px-1 py-0.5 rounded"
                style={{ color: undoStack.length ? colors.text : colors.textMuted }}><Undo2 className="w-3 h-3" />Undo</button>
              <button onClick={redo} disabled={!redoStack.length} className="flex items-center gap-1 text-[10px] px-1 py-0.5 rounded"
                style={{ color: redoStack.length ? colors.text : colors.textMuted }}><Redo2 className="w-3 h-3" />Redo</button>
              <button onClick={clearDrawings} className="flex items-center gap-1 text-[10px] px-1 py-0.5 rounded" style={{ color: colors.down }}>
                <Trash2 className="w-3 h-3" />Clear All</button>
              <button onClick={captureScreenshot} className="flex items-center gap-1 text-[10px] px-1 py-0.5 rounded" style={{ color: colors.text }}>
                <Camera className="w-3 h-3" />Screenshot</button>
            </div>
          </div>
        )}

        {/* ===== LEFT SIDE PANELS ===== */}

        {/* INDICATORS PANEL */}
        <SidePanel title="Indicators" show={showIndicatorPanel} onClose={() => setShowIndicatorPanel(false)} width="w-48" colors={colors}>
          {Object.entries(INDICATORS).map(([group, inds]) => (
            <div key={group} className="mb-2">
              <div className="text-[9px] uppercase font-bold mb-1" style={{ color: colors.textMuted }}>{group}</div>
              {inds.map(ind => (
                <label key={ind.value} className="flex items-center gap-2 py-0.5 cursor-pointer text-xs">
                  <input type="checkbox" checked={activeIndicators.includes(ind.value)} onChange={() => toggleIndicator(ind.value)}
                    className="rounded" style={{ accentColor: colors.accent }} />
                  <span>{ind.label}</span>
                </label>
              ))}
            </div>
          ))}
        </SidePanel>

        {/* AI ANALYSIS PANEL */}
        <SidePanel title="AI Analysis" show={showAIPanel} onClose={() => setShowAIPanel(false)} width="w-72" colors={colors}>
          <button onClick={runAIAnalysis} disabled={aiLoading}
            className="w-full py-1.5 rounded text-xs font-semibold mb-2 disabled:opacity-50"
            style={{ background: colors.accent, color: '#fff' }}>
            {aiLoading ? 'Analyzing...' : 'Run Analysis'}
          </button>
          {aiResults && (
            <div className="space-y-2 text-[11px]">
              {/* Patterns */}
              <div className="rounded p-2" style={{ background: colors.bg }}>
                <div className="font-bold mb-1">Patterns ({aiResults.patterns.length})</div>
                {aiResults.patterns.length === 0 ? <div style={{ color: colors.textMuted }}>No patterns detected</div> :
                  aiResults.patterns.map((p, i) => (
                    <div key={i} className="flex justify-between py-0.5">
                      <span>{p.name}</span>
                      <span className={`px-1 rounded text-[9px] ${p.type === 'bullish' ? 'text-green-400' : p.type === 'bearish' ? 'text-red-400' : 'text-yellow-400'}`}>
                        {p.confidence}%
                      </span>
                    </div>
                  ))}
              </div>
              {/* S/R */}
              <div className="rounded p-2" style={{ background: colors.bg }}>
                <div className="font-bold mb-1">Support & Resistance</div>
                {aiResults.resistance.map((r, i) => (
                  <div key={`r${i}`} className="flex justify-between py-0.5"><span className="text-red-400">R: ${r.price.toFixed(2)}</span><span className="text-[9px]">{r.strength.toFixed(0)}%</span></div>
                ))}
                {aiResults.support.map((s, i) => (
                  <div key={`s${i}`} className="flex justify-between py-0.5"><span className="text-green-400">S: ${s.price.toFixed(2)}</span><span className="text-[9px]">{s.strength.toFixed(0)}%</span></div>
                ))}
              </div>
              {/* Trend */}
              <div className="rounded p-2" style={{ background: colors.bg }}>
                <div className="font-bold mb-1">Trend</div>
                <div className="flex justify-between"><span>Direction</span><span>{aiResults.trend.direction === 'Bullish' ? '🟢' : aiResults.trend.direction === 'Bearish' ? '🔴' : '🟡'} {aiResults.trend.direction}</span></div>
                <div className="flex justify-between"><span>Strength</span><span>{aiResults.trend.strength}%</span></div>
                <div className="flex justify-between"><span>ADX</span><span>{aiResults.trend.adx}</span></div>
              </div>
              {/* Forecast */}
              <div className="rounded p-2" style={{ background: colors.bg }}>
                <div className="font-bold mb-1">10-Day Forecast</div>
                <div className="flex justify-between"><span>Target</span><span style={{ color: aiResults.forecast.change > 0 ? colors.up : colors.down }}>${aiResults.forecast.target}</span></div>
                <div className="flex justify-between"><span>Change</span><span>{aiResults.forecast.change}%</span></div>
                <div className="flex justify-between"><span>Range</span><span>${aiResults.forecast.range.lower} - ${aiResults.forecast.range.upper}</span></div>
                <div className="flex justify-between"><span>Confidence</span><span>{aiResults.forecast.confidence}%</span></div>
              </div>
              {/* Volatility */}
              <div className="rounded p-2" style={{ background: colors.bg }}>
                <div className="font-bold mb-1">Volatility</div>
                <div className="flex justify-between"><span>Historical</span><span>{aiResults.volatility.historical}%</span></div>
                <div className="flex justify-between"><span>ATR %</span><span>{aiResults.volatility.atrPct}%</span></div>
                <div className="flex justify-between"><span>Regime</span><span>{aiResults.volatility.regime}</span></div>
              </div>
              {/* Breakout */}
              <div className="rounded p-2" style={{ background: colors.bg }}>
                <div className="font-bold mb-1">Breakout Prediction</div>
                <div className="flex justify-between"><span>Probability</span><span>{aiResults.breakout.probability}%</span></div>
                <div className="flex justify-between"><span>Direction</span><span className={aiResults.breakout.direction === 'bullish' ? 'text-green-400' : 'text-red-400'}>{aiResults.breakout.direction}</span></div>
                <div className="flex justify-between"><span>BB Squeeze</span><span>{aiResults.breakout.squeeze ? '🔥 Yes' : 'No'}</span></div>
              </div>
              {/* Anomalies */}
              <div className="rounded p-2" style={{ background: colors.bg }}>
                <div className="font-bold mb-1">Anomalies ({aiResults.anomalies.length})</div>
                {aiResults.anomalies.length === 0 ? <div style={{ color: colors.textMuted }}>None detected</div> :
                  aiResults.anomalies.map((a, i) => (
                    <div key={i} className="flex justify-between py-0.5">
                      <span>{a.type}</span><span className="text-[9px]" style={{ color: a.severity === 'high' ? colors.down : '#FF9800' }}>{a.date}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </SidePanel>

        {/* RISK CALCULATOR */}
        <SidePanel title="Risk Calculator" show={showRiskCalc} onClose={() => setShowRiskCalc(false)} width="w-56" colors={colors}>
          <div className="space-y-1.5 text-xs">
            {[{ l: 'Account $', k: 'account' }, { l: 'Risk %', k: 'riskPct' }, { l: 'Entry $', k: 'entry' }, { l: 'Stop $', k: 'stop' }, { l: 'Target $', k: 'target' }].map(f => (
              <div key={f.k}>
                <label className="text-[10px]" style={{ color: colors.textMuted }}>{f.l}</label>
                <input type="number" value={riskInputs[f.k]} onChange={e => setRiskInputs(prev => ({ ...prev, [f.k]: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-2 py-1 rounded text-xs" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
              </div>
            ))}
            <button onClick={autoFillRisk} className="w-full py-1 rounded text-[10px]" style={{ background: colors.border, color: colors.text }}>Auto (ATR-based)</button>
            <div className="border-t pt-2 space-y-1" style={{ borderColor: colors.border }}>
              {[{ l: 'Risk Amount', v: `$${riskResults.riskAmt}` }, { l: 'Shares', v: riskResults.shares }, { l: 'Position $', v: `$${riskResults.posValue}` },
                { l: 'Profit', v: `$${riskResults.profit}`, c: colors.up }, { l: 'R:R', v: riskResults.rr }].map(r => (
                <div key={r.l} className="flex justify-between text-[11px]"><span style={{ color: colors.textMuted }}>{r.l}</span><span style={r.c ? { color: r.c } : {}}>{r.v}</span></div>
              ))}
              <div className="h-2 rounded overflow-hidden flex" style={{ background: colors.border }}>
                <div style={{ width: `${100 / (1 + parseFloat(riskResults.rr))}%`, background: colors.down }} className="h-full" />
                <div style={{ width: `${100 * parseFloat(riskResults.rr) / (1 + parseFloat(riskResults.rr))}%`, background: colors.up }} className="h-full" />
              </div>
            </div>
          </div>
        </SidePanel>

        {/* ALERTS PANEL */}
        <SidePanel title="Alerts" show={showAlerts} onClose={() => setShowAlerts(false)} width="w-56" colors={colors}>
          <div className="space-y-1.5 text-xs">
            <select value={alertForm.type} onChange={e => setAlertForm(f => ({ ...f, type: e.target.value }))}
              className="w-full px-2 py-1 rounded text-xs" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}>
              <option value="above">Price Above</option><option value="below">Price Below</option>
            </select>
            <input type="number" placeholder="Price" value={alertForm.value} onChange={e => setAlertForm(f => ({ ...f, value: e.target.value }))}
              className="w-full px-2 py-1 rounded text-xs" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
            <select value={alertForm.sound} onChange={e => setAlertForm(f => ({ ...f, sound: e.target.value }))}
              className="w-full px-2 py-1 rounded text-xs" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}>
              {ALERT_SOUNDS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button onClick={addAlert} className="w-full py-1 rounded text-xs" style={{ background: colors.accent, color: '#fff' }}>Add Alert</button>
            <div className="border-t pt-2 space-y-1" style={{ borderColor: colors.border }}>
              {alerts.filter(a => a.ticker === ticker).map(a => (
                <div key={a.id} className="flex items-center justify-between py-0.5 text-[11px]"
                  style={{ opacity: a.active ? 1 : 0.5 }}>
                  <span>{a.type} ${a.value.toFixed(2)}</span>
                  <div className="flex gap-0.5">
                    {a.triggered && <span className="text-[9px] px-1 rounded" style={{ background: colors.down + '30', color: colors.down }}>!</span>}
                    <button onClick={() => toggleAlert(a.id)}>{a.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}</button>
                    <button onClick={() => deleteAlert(a.id)} style={{ color: colors.down }}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </SidePanel>

        {/* TRADE JOURNAL */}
        <SidePanel title="Trade Journal" show={showJournal} onClose={() => setShowJournal(false)} width="w-64" colors={colors}>
          <div className="flex gap-1 mb-2">
            {['journal', 'positions'].map(tab => (
              <button key={tab} onClick={() => setJournalTab(tab)}
                className={`px-2 py-0.5 text-[10px] rounded ${journalTab === tab ? 'bg-blue-600 text-white' : ''}`}
                style={journalTab !== tab ? { color: colors.textMuted } : {}}>{tab === 'journal' ? 'Journal' : 'Positions'}</button>
            ))}
          </div>
          {journalTab === 'journal' ? (
            <div className="space-y-1.5 text-xs">
              <input type="text" placeholder="Ticker" value={tradeForm.ticker} onChange={e => setTradeForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))}
                className="w-full px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
              <select value={tradeForm.type} onChange={e => setTradeForm(f => ({ ...f, type: e.target.value }))}
                className="w-full px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}>
                <option value="buy">Buy</option><option value="sell">Sell</option>
              </select>
              <div className="flex gap-1">
                <input type="number" placeholder="Entry" value={tradeForm.entry} onChange={e => setTradeForm(f => ({ ...f, entry: e.target.value }))} className="w-1/2 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
                <input type="number" placeholder="Exit" value={tradeForm.exit} onChange={e => setTradeForm(f => ({ ...f, exit: e.target.value }))} className="w-1/2 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
              </div>
              <div className="flex gap-1">
                <input type="number" placeholder="Qty" value={tradeForm.qty} onChange={e => setTradeForm(f => ({ ...f, qty: e.target.value }))} className="w-1/2 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
                <input type="text" placeholder="Notes..." value={tradeForm.notes} onChange={e => setTradeForm(f => ({ ...f, notes: e.target.value }))} className="w-1/2 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
              </div>
              <button onClick={addTrade} className="w-full py-1 rounded" style={{ background: colors.accent, color: '#fff' }}>Log Trade</button>
              <div className="border-t pt-2 space-y-1" style={{ borderColor: colors.border }}>
                {trades.map(t => (
                  <div key={t.id} className="rounded p-1.5 text-[10px]" style={{ background: colors.bg }}>
                    <div className="flex justify-between">
                      <span className="font-bold">{t.ticker} <span className={t.type === 'buy' ? 'text-green-400' : 'text-red-400'}>{t.type.toUpperCase()}</span></span>
                      <button onClick={() => deleteTrade(t.id)} style={{ color: colors.down }}><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <div className="flex justify-between"><span>{t.qty}x ${t.entry}{t.exit ? ` → $${t.exit}` : ''}</span>
                      {t.pnl !== null && <span style={{ color: t.pnl >= 0 ? colors.up : colors.down }}>{t.pnl >= 0 ? '+' : ''}${t.pnl.toFixed(2)}</span>}
                    </div>
                    {t.notes && <div style={{ color: colors.textMuted }}>{t.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-1.5 text-xs">
              <input type="text" placeholder="Ticker" value={posForm.ticker} onChange={e => setPosForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))}
                className="w-full px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
              <select value={posForm.type} onChange={e => setPosForm(f => ({ ...f, type: e.target.value }))}
                className="w-full px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}>
                <option value="long">Long</option><option value="short">Short</option>
              </select>
              <div className="flex gap-1">
                <input type="number" placeholder="Entry" value={posForm.entry} onChange={e => setPosForm(f => ({ ...f, entry: e.target.value }))} className="w-1/2 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
                <input type="number" placeholder="Qty" value={posForm.qty} onChange={e => setPosForm(f => ({ ...f, qty: e.target.value }))} className="w-1/2 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
              </div>
              <div className="flex gap-1">
                <input type="number" placeholder="Stop Loss" value={posForm.stopLoss} onChange={e => setPosForm(f => ({ ...f, stopLoss: e.target.value }))} className="w-1/2 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
                <input type="number" placeholder="Target" value={posForm.target} onChange={e => setPosForm(f => ({ ...f, target: e.target.value }))} className="w-1/2 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
              </div>
              <button onClick={addPosition} className="w-full py-1 rounded" style={{ background: colors.accent, color: '#fff' }}>Open Position</button>
              <div className="border-t pt-2 space-y-1" style={{ borderColor: colors.border }}>
                {positions.map(p => (
                  <div key={p.id} className="rounded p-1.5 text-[10px]" style={{ background: colors.bg }}>
                    <div className="flex justify-between">
                      <span className="font-bold">{p.ticker} <span className={p.type === 'long' ? 'text-green-400' : 'text-red-400'}>{p.type.toUpperCase()}</span></span>
                      <button onClick={() => closePosition(p.id)} className="text-[9px] px-1 rounded" style={{ background: colors.down + '30', color: colors.down }}>Close</button>
                    </div>
                    <div>{p.qty}x @ ${p.entry} {p.stopLoss ? `SL: $${p.stopLoss}` : ''} {p.target ? `TP: $${p.target}` : ''}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SidePanel>

        {/* PERFORMANCE */}
        <SidePanel title="Performance" show={showPerformance} onClose={() => setShowPerformance(false)} width="w-52" colors={colors}>
          {!perfStats ? <div className="text-xs" style={{ color: colors.textMuted }}>Log trades to see stats</div> : (
            <div className="space-y-1 text-[11px]">
              {[{ l: 'Total Trades', v: perfStats.total }, { l: 'Win Rate', v: `${perfStats.winRate}%` },
                { l: 'Profit Factor', v: perfStats.profitFactor }, { l: 'Total P&L', v: `$${perfStats.totalPnl}`, c: parseFloat(perfStats.totalPnl) >= 0 ? colors.up : colors.down },
                { l: 'Avg Win', v: `$${perfStats.avgWin}`, c: colors.up }, { l: 'Avg Loss', v: `$${perfStats.avgLoss}`, c: colors.down },
                { l: 'Best Trade', v: `$${perfStats.bestTrade}` }, { l: 'Worst Trade', v: `$${perfStats.worstTrade}` },
                { l: 'Max Drawdown', v: `$${perfStats.maxDrawdown}` },
              ].map(r => (
                <div key={r.l} className="flex justify-between"><span style={{ color: colors.textMuted }}>{r.l}</span><span style={r.c ? { color: r.c } : {}}>{r.v}</span></div>
              ))}
              <div className="h-2 rounded overflow-hidden flex mt-1" style={{ background: colors.border }}>
                <div style={{ width: `${perfStats.winRate}%`, background: colors.up }} className="h-full" />
                <div style={{ width: `${100 - parseFloat(perfStats.winRate)}%`, background: colors.down }} className="h-full" />
              </div>
            </div>
          )}
        </SidePanel>

        {/* COMPARISON */}
        <SidePanel title="Compare" show={showComparison} onClose={() => setShowComparison(false)} width="w-52" colors={colors}>
          <div className="space-y-1.5 text-xs">
            <select value={comparisonMode} onChange={e => setComparisonMode(e.target.value)}
              className="w-full px-2 py-1 rounded text-xs" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}>
              {COMPARISON_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <div className="flex gap-1">
              <input type="text" placeholder="Add ticker..." value={comparisonInput}
                onChange={e => setComparisonInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && addComparisonTicker()}
                className="flex-1 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
              <button onClick={addComparisonTicker} className="px-2 rounded" style={{ background: colors.accent, color: '#fff' }}><Plus className="w-3 h-3" /></button>
            </div>
            {comparisonTickers.map((sym, i) => (
              <div key={sym} className="flex items-center justify-between py-0.5">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ background: COMPARISON_COLORS[i % COMPARISON_COLORS.length] }} />
                  <span>{sym}</span>
                </div>
                <button onClick={() => removeComparisonTicker(sym)} style={{ color: colors.down }}><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </SidePanel>

        {/* WATCHLIST */}
        <SidePanel title="Watchlist" show={showWatchlist} onClose={() => setShowWatchlist(false)} width="w-44" colors={colors}>
          <div className="space-y-1.5 text-xs">
            <div className="flex gap-1">
              <input type="text" placeholder="Add symbol..." value={watchlistInput}
                onChange={e => setWatchlistInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && addToWatchlist()}
                className="flex-1 px-2 py-1 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
              <button onClick={addToWatchlist} className="px-2 rounded" style={{ background: colors.accent, color: '#fff' }}><Plus className="w-3 h-3" /></button>
            </div>
            {watchlist.map(sym => (
              <div key={sym} className="flex items-center justify-between py-0.5 cursor-pointer hover:opacity-80"
                onClick={() => { setTicker(sym); setTickerInput(sym); }}>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3" style={{ color: sym === ticker ? colors.accent : colors.textMuted }} />
                  <span style={{ color: sym === ticker ? colors.accent : colors.text }}>{sym}</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); removeFromWatchlist(sym); }} style={{ color: colors.down }}><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </SidePanel>

        {/* ===== CHART AREA ===== */}
        <div
          className={`flex-1 overflow-hidden ${layout === 4 ? 'grid grid-cols-2 grid-rows-2' : layout === 2 ? 'flex flex-row' : layout === 3 ? 'flex flex-col' : 'flex flex-col'}`}
          style={{ minWidth: 0 }}
        >
        {/* ── PANE 1 (main chart) ── */}
        <div className={`flex flex-col ${layout === 4 ? '' : 'flex-1'}`}
          style={{ minWidth: 0, minHeight: 0, borderRight: layout === 2 ? `1px solid ${colors.border}` : 'none', borderBottom: layout === 3 ? `1px solid ${colors.border}` : 'none' }}
          onClick={handleChartClick}>
          <div ref={chartContainerRef} className="flex-1 relative overflow-hidden" style={{ minHeight: 0 }}>
            {/* ── Pane 1 info overlay ── */}
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-1.5 px-2 py-1"
              style={{ background: colors.panel + 'e0', borderBottom: `1px solid ${colors.border}` }}
              onClick={e => e.stopPropagation()}>
              {/* Ticker label */}
              <span className="text-xs font-bold flex-shrink-0" style={{ color: colors.accent }}>{ticker}</span>
              {/* Ticker input + Go */}
              <input type="text" value={tickerInput} onChange={e => setTickerInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && handleLoadTicker()}
                className="border rounded px-1 py-0.5 text-[10px] w-14 uppercase focus:outline-none flex-shrink-0"
                style={{ background: colors.bg, borderColor: colors.border, color: colors.text }} placeholder="SYM" />
              <button onClick={handleLoadTicker}
                className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-[10px] hover:bg-blue-700 flex-shrink-0">Go</button>
              {/* Preset symbols */}
              <div className="hidden md:flex gap-0.5 flex-shrink-0">
                {TICKER_PRESETS.slice(0, 5).map(sym => (
                  <button key={sym} onClick={() => { setTicker(sym); setTickerInput(sym); }}
                    className="px-1.5 py-0.5 text-[10px] rounded transition"
                    style={{ color: sym === ticker ? colors.accent : colors.textMuted, background: sym === ticker ? colors.accent + '20' : 'transparent' }}>
                    {sym}
                  </button>
                ))}
              </div>
              {/* OHLCV */}
              {legendData.close && (
                <div className="flex items-center gap-1 text-[10px] pointer-events-none">
                  <span style={{ color: colors.textMuted }}>O</span>
                  <span style={{ color: colors.text }}>{legendData.open?.toFixed(2)}</span>
                  <span style={{ color: colors.textMuted }}>H</span>
                  <span style={{ color: colors.text }}>{legendData.high?.toFixed(2)}</span>
                  <span style={{ color: colors.textMuted }}>L</span>
                  <span style={{ color: colors.text }}>{legendData.low?.toFixed(2)}</span>
                  <span style={{ color: colors.textMuted }}>C</span>
                  <span style={{ color: legendData.close >= legendData.open ? colors.up : colors.down }}>
                    {legendData.close?.toFixed(2)}
                  </span>
                  {legendData.volume > 0 && (
                    <><span style={{ color: colors.textMuted }}>Vol</span>
                    <span style={{ color: colors.text }}>{(legendData.volume / 1e6).toFixed(1)}M</span></>
                  )}
                </div>
              )}
            </div>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center z-10" style={{ background: colors.bg + 'cc' }}>
                <div style={{ color: colors.textMuted }}>Loading {ticker} chart data...</div>
              </div>
            )}
            {chartError && (
              <div className="absolute inset-0 flex items-center justify-center z-20 flex-col gap-2" style={{ background: colors.bg }}>
                <div className="text-sm font-bold text-red-400">Chart Init Error</div>
                <div className="text-xs px-4 text-center max-w-md" style={{ color: colors.textMuted }}>{chartError}</div>
                <button onClick={() => { setChartError(null); loadData(ticker, period); }}
                  className="mt-2 px-3 py-1 text-xs rounded" style={{ background: colors.accent, color: '#fff' }}>Retry</button>
              </div>
            )}
            {activeTool !== 'cursor' && activeTool !== 'crosshair' && (
              <div className="absolute top-8 left-2 text-[10px] px-2 py-1 rounded z-20" style={{ background: colors.accent, color: '#fff' }}>
                {drawingStart ? 'Click to set end point' : `${ALL_DRAWING_TOOLS.find(t => t.value === activeTool)?.label || activeTool}: Click to start`}
              </div>
            )}
          </div>
          {/* RSI sub-chart */}
          {activeIndicators.includes('rsi') && (
            <div className="border-t flex-shrink-0" style={{ borderColor: colors.border }}>
              <div className="text-xs px-2 py-1" style={{ color: colors.textMuted, background: colors.panel }}>RSI (14)</div>
              <div ref={rsiContainerRef} style={{ height: 160 }} />
            </div>
          )}
          {/* MACD sub-chart */}
          {activeIndicators.includes('macd') && (
            <div className="border-t flex-shrink-0" style={{ borderColor: colors.border }}>
              <div className="text-xs px-2 py-1" style={{ color: colors.textMuted, background: colors.panel }}>MACD (12,26,9)</div>
              <div ref={macdContainerRef} style={{ height: 160 }} />
            </div>
          )}
          {/* Dynamic sub-charts */}
          {subChartIndicators.filter(ind => activeIndicators.includes(ind)).map(ind => (
            <div key={ind} className="border-t flex-shrink-0" style={{ borderColor: colors.border }}>
              <div className="text-xs px-2 py-1" style={{ color: colors.textMuted, background: colors.panel }}>
                {ALL_INDICATORS.find(i => i.value === ind)?.label || ind}
              </div>
              <div id={`subchart-${ind}`} style={{ height: 160 }} />
            </div>
          ))}
        </div>{/* end pane 1 */}

        {/* ── PANE 2 (2H, 2V, 2×2) ── */}
        {layout >= 2 && (
          <MiniChartPane
            ref={pane2Ref}
            key={`pane2-${theme}`}
            initialTicker={pane2Ticker}
            initialPeriod={period}
            theme={theme}
            onClose={() => setLayout(1)}
            onRangeChange={(range) => {
              if (!mainSyncingRef.current && chartRef.current) {
                mainSyncingRef.current = true;
                try { chartRef.current.timeScale().setVisibleLogicalRange(range); } catch {}
                pane3Ref.current?.setTimeRange(range);
                pane4Ref.current?.setTimeRange(range);
                mainSyncingRef.current = false;
              }
            }}
          />
        )}

        {/* ── PANE 3 & 4 (2×2 only) ── */}
        {layout === 4 && (
          <MiniChartPane
            ref={pane3Ref}
            key={`pane3-${theme}`}
            initialTicker={pane3Ticker}
            initialPeriod={period}
            theme={theme}
            onRangeChange={(range) => {
              if (!mainSyncingRef.current && chartRef.current) {
                mainSyncingRef.current = true;
                try { chartRef.current.timeScale().setVisibleLogicalRange(range); } catch {}
                pane2Ref.current?.setTimeRange(range);
                pane4Ref.current?.setTimeRange(range);
                mainSyncingRef.current = false;
              }
            }}
          />
        )}
        {layout === 4 && (
          <MiniChartPane
            ref={pane4Ref}
            key={`pane4-${theme}`}
            initialTicker={pane4Ticker}
            initialPeriod={period}
            theme={theme}
            onRangeChange={(range) => {
              if (!mainSyncingRef.current && chartRef.current) {
                mainSyncingRef.current = true;
                try { chartRef.current.timeScale().setVisibleLogicalRange(range); } catch {}
                pane2Ref.current?.setTimeRange(range);
                pane3Ref.current?.setTimeRange(range);
                mainSyncingRef.current = false;
              }
            }}
          />
        )}

        </div>{/* end chart area grid/flex */}

      </div>

      {/* ===== MODALS ===== */}
      <Modal title="Keyboard Shortcuts" show={showShortcuts} onClose={() => setShowShortcuts(false)} colors={colors}>
        <div className="grid grid-cols-2 gap-1 text-xs">
          {KEYBOARD_SHORTCUTS.map(s => (
            <div key={s.key} className="flex justify-between py-0.5 px-2 rounded" style={{ background: colors.bg }}>
              <kbd className="px-1.5 py-0.5 rounded text-[10px] font-mono" style={{ background: colors.border }}>{s.key}</kbd>
              <span style={{ color: colors.textMuted }}>{s.desc}</span>
            </div>
          ))}
        </div>
      </Modal>

      <Modal title="Go to Date" show={showGoToDate} onClose={() => setShowGoToDate(false)} colors={colors} width="max-w-sm">
        <div className="flex gap-2">
          <input type="date" value={goToDateValue} onChange={e => setGoToDateValue(e.target.value)}
            className="flex-1 px-3 py-2 rounded" style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }} />
          <button onClick={goToDate} className="px-4 py-2 rounded" style={{ background: colors.accent, color: '#fff' }}>Go</button>
        </div>
      </Modal>

      {/* ===== CONTEXT MENU ===== */}
      {showContextMenu && (
        <div className="fixed z-[200] rounded shadow-lg py-1 text-xs min-w-[160px]"
          style={{ left: showContextMenu.x, top: showContextMenu.y, background: colors.panel, border: `1px solid ${colors.border}` }}
          onClick={e => e.stopPropagation()}>
          {[
            { label: 'Fit Content', action: () => chartRef.current?.timeScale().fitContent() },
            { label: 'Add Horizontal Line', action: () => setActiveTool('horizontal') },
            { label: 'Add Trendline', action: () => setActiveTool('trendline') },
            { label: 'Fibonacci Retracement', action: () => setActiveTool('fibonacci') },
            { label: 'Run AI Analysis', action: runAIAnalysis },
            { label: 'Set Alert Here', action: () => setShowAlerts(true) },
            { label: 'Go to Date', action: () => setShowGoToDate(true) },
            { label: 'Start Replay', action: toggleReplay },
            { label: 'Screenshot', action: captureScreenshot },
            { label: 'Clear Drawings', action: clearDrawings },
            { label: 'Switch Theme', action: toggleTheme },
          ].map(item => (
            <button key={item.label} onClick={() => { item.action(); setShowContextMenu(null); }}
              className="w-full text-left px-3 py-1.5 hover:opacity-80 transition"
              style={{ color: colors.text }}>
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* ===== TOAST NOTIFICATIONS ===== */}
      <div className="fixed bottom-4 right-4 z-[300] space-y-2">
        {toasts.map(t => (
          <div key={t.id} className="px-3 py-2 rounded shadow-lg text-xs animate-pulse"
            style={{ background: t.type === 'error' ? colors.down : t.type === 'warning' ? '#FF9800' : colors.accent, color: '#fff' }}>
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}
